import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	FormControlLabel,
	InputAdornment,
	Modal,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	withStyles,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';
import EmailIcon from '@material-ui/icons/Email';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import AssignmentIcon from '@material-ui/icons/AssignmentOutlined';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFileOutlined';

const CUSTOMER_TABLE_COLUMNS = 'minmax(300px, 1fr) 140px 140px 100px';
const QUOTE_TABLE_COLUMNS = '1.4fr 0.9fr 0.8fr 0.8fr 0.9fr 84px 32px 32px';
const INVOICE_TABLE_COLUMNS = '1fr 1.3fr 0.8fr 0.8fr 80px 84px';
const ITEM_EDITOR_COLUMNS = 'minmax(0, 1fr) 80px 110px 90px 30px';
const ORDER_TABLE_COLUMNS = '1.4fr 0.8fr 0.9fr 0.8fr 1.1fr 32px 32px';
const PICK_TABLE_COLUMNS = '1.4fr 0.8fr 0.6fr 0.7fr 1fr 72px';
const PICK_ITEM_COLUMNS = 'minmax(0, 1fr) 64px 84px 84px 100px 90px';

const emptyCustomer = {
	first_name: '',
	last_name: '',
	company_name: '',
	contact_name: '',
	phone: '',
	fax: '',
	email: '',
	accounts_payable_email: '',
	quote_delivery_method: 'email',
	invoice_delivery_method: 'email',
	billing_address_1: '',
	billing_address_2: '',
	billing_city: '',
	billing_state: '',
	billing_zip: '',
	notes: '',
	locations: [
		{
			address_1: '',
			address_2: '',
			city: '',
			state: '',
			zip: '',
		},
	],
	billing_same_as_default_address: true,
};

const emptyProduct = {
	item_code: '',
	item_name: '',
	upc_code: '',
	price: '',
	unit_weight: '',
};

const emptyQuote = {
	customer_id: '',
	customer_location_id: '',
	items: [{ product_id: '', amount: '', unit_price: '' }],
	shipping_handling: '',
	payment_terms: 'net15',
	expected_delivery_date: '',
	notes: '',
};

const emptyOrder = {
	customer_id: '',
	customer_location_id: '',
	quote_id: '',
	quote_number: '',
	received_via: 'email',
	received_date: '',
	expected_delivery_date: '',
	payment_terms: 'net15',
	shipping_handling: '',
	notes: '',
	items: [{ product_id: '', amount: '', unit_price: '' }],
};

const deliveryOptions = ['email', 'fax', 'mail'];
const receivedViaOptions = ['email', 'fax', 'phone'];
// Mirrors the server's allowed attachment types and size cap.
const attachmentExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.tif', '.tiff', '.doc', '.docx'];
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

const orderStatusStyles = {
	Received: { background: '#e9edf3', color: '#3c4f6b' },
	Picking: { background: '#fff0d7', color: '#8a5a00' },
	Packed: { background: '#e3eefc', color: '#1d4f91' },
	Invoiced: { background: '#dff4e6', color: '#1f6f47' },
};

// Local calendar date, so an evening entry doesn't land on tomorrow.
const todayIso = () => {
	const date = new Date();
	date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
	return date.toISOString().substring(0, 10);
};
const paymentTerms = ['net15', 'net30', 'net45'];

const statusColor = {
	Open: '#fff0d7',
	Paid: '#dff4e6',
};

const styles = () => ({
	page: {
		minHeight: '100vh',
		padding: '8px 32px 40px',
		background: '#f8f7f5',
		color: '#1a1d1a',
	},
	shell: {
		maxWidth: 1180,
		margin: '0 auto',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		columnGap: 16,
		minHeight: 60,
		marginBottom: 12,
		borderBottom: '1px solid #e2e0da',
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 14,
	},
	headerActions: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 6,
	},
	yearSelect: {
		minWidth: 130,
		'& .MuiOutlinedInput-root': {
			borderRadius: 8,
			background: '#ffffff',
			fontSize: 13,
		},
		'& select': {
			paddingTop: 7,
			paddingBottom: 7,
		},
	},
	archivedNote: {
		fontSize: 12,
		fontWeight: 500,
		color: '#9a7a31',
	},
	setupLink: {
		minHeight: 34,
		padding: '0 12px',
		borderRadius: 8,
		fontSize: 13,
		fontWeight: 500,
		textTransform: 'none',
		color: '#505850',
		'&:hover': {
			background: '#eeecea',
		},
	},
	setupLinkActive: {
		background: '#dedbd3',
		color: '#1a1d1a',
		fontWeight: 600,
		'&:hover': {
			background: '#d6d3ca',
		},
	},
	setupDivider: {
		width: 1,
		height: 20,
		margin: '0 8px',
		background: '#e2e0da',
	},
	eyebrow: {
		textTransform: 'uppercase',
		letterSpacing: 1,
		fontSize: 11,
		fontWeight: 600,
		color: '#9a7a31',
		marginBottom: 4,
	},
	title: {
		fontSize: 20,
		fontWeight: 600,
		letterSpacing: -0.2,
		color: '#1a1d1a',
	},
	titleButton: {
		minWidth: 0,
		padding: '2px 6px',
		marginLeft: -6,
		borderRadius: 8,
		fontSize: 20,
		fontWeight: 600,
		lineHeight: 1.4,
		letterSpacing: -0.2,
		textTransform: 'none',
		color: '#1a1d1a',
		'&:hover': {
			background: '#eeecea',
		},
	},
	titleMuted: {
		fontWeight: 400,
		color: '#505850',
	},
	subtitle: {
		marginTop: 4,
		fontSize: 13,
		color: '#737873',
		maxWidth: 680,
		lineHeight: 1.5,
	},
	headerMetric: {
		minWidth: 110,
		padding: '10px 14px',
		borderRadius: 10,
		background: '#ffffff',
		border: '1px solid #e2e0da',
		textAlign: 'center',
	},
	metricValue: {
		fontSize: 20,
		fontWeight: 600,
		color: '#1a1d1a',
	},
	metricLabel: {
		fontSize: 10,
		fontWeight: 600,
		color: '#8a918a',
		textTransform: 'uppercase',
		letterSpacing: 0.7,
	},
	tabs: {
		minHeight: 60,
		marginLeft: 10,
		'& .MuiTabs-indicator': {
			backgroundColor: '#1a1d1a',
			height: 2,
		},
	},
	tab: {
		minHeight: 60,
		minWidth: 0,
		padding: '0 14px',
		fontSize: 14,
		fontWeight: 400,
		textTransform: 'none',
		color: '#737873',
		'&.Mui-selected': {
			color: '#1a1d1a',
			fontWeight: 500,
		},
	},
	card: {
		borderRadius: 12,
		boxShadow: 'none',
		border: '1px solid #e8e6e0',
		overflow: 'hidden',
	},
	cardHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 14,
		marginBottom: 14,
	},
	sectionTitle: {
		fontSize: 17,
		fontWeight: 600,
		color: '#1a1d1a',
		letterSpacing: -0.1,
	},
	sectionHint: {
		marginTop: 3,
		fontSize: 13,
		color: '#737873',
	},
	compactField: {
		marginBottom: 10,
	},
	fieldRow: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 10,
	},
	locationCard: {
		marginBottom: 10,
		padding: '10px 12px 2px',
		borderRadius: 10,
		background: '#faf9f7',
		border: '1px solid #eceae4',
		'& .MuiOutlinedInput-root': {
			background: '#ffffff',
		},
	},
	locationHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		minHeight: 30,
		marginBottom: 8,
	},
	locationTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		fontSize: 13,
		fontWeight: 600,
		color: '#1a1d1a',
	},
	defaultBadge: {
		padding: '2px 6px',
		borderRadius: 5,
		background: '#efeee9',
		color: '#737873',
		fontSize: 11,
		fontWeight: 500,
	},
	addressCityRow: {
		display: 'grid',
		gridTemplateColumns: '1fr 90px 120px',
		gap: 10,
	},
	deliveryRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		marginBottom: 8,
	},
	deliveryLabel: {
		width: 70,
		fontSize: 13,
		color: '#505850',
	},
	segmented: {
		display: 'inline-flex',
		padding: 2,
		borderRadius: 8,
		background: '#efeee9',
	},
	segmentButton: {
		minWidth: 72,
		minHeight: 30,
		padding: '0 14px',
		borderRadius: 6,
		fontSize: 13,
		fontWeight: 500,
		textTransform: 'capitalize',
		color: '#737873',
		'&:hover': {
			background: 'rgba(255, 255, 255, 0.6)',
		},
	},
	segmentButtonActive: {
		background: '#ffffff',
		color: '#1a1d1a',
		boxShadow: '0 1px 2px rgba(10, 12, 10, 0.12)',
		'&:hover': {
			background: '#ffffff',
		},
	},
	checkboxLabel: {
		marginLeft: -6,
		'& .MuiFormControlLabel-label': {
			fontSize: 13,
			color: '#505850',
		},
	},
	primaryButton: {
		minHeight: 38,
		borderRadius: 8,
		background: '#1a1d1a',
		color: '#ffffff',
		fontSize: 13,
		fontWeight: 500,
		textTransform: 'none',
		boxShadow: 'none',
		'&:hover': {
			background: '#2e322e',
			boxShadow: 'none',
		},
		'&:disabled': {
			background: '#d0cec9',
			color: '#8a908a',
		},
	},
	secondaryButton: {
		minHeight: 34,
		borderRadius: 8,
		fontSize: 13,
		fontWeight: 500,
		textTransform: 'none',
		color: '#374038',
		borderColor: '#d4d2cc',
	},
	row: {
		display: 'grid',
		gridTemplateColumns: QUOTE_TABLE_COLUMNS,
		gap: 16,
		alignItems: 'center',
		padding: '10px 20px',
		borderBottom: '1px solid #eceae4',
		'&:last-child': {
			borderBottom: 'none',
		},
	},
	invoiceRow: {
		display: 'grid',
		gridTemplateColumns: INVOICE_TABLE_COLUMNS,
		gap: 16,
		alignItems: 'center',
		padding: '14px 20px',
		borderBottom: '1px solid #eceae4',
		'&:last-child': {
			borderBottom: 'none',
		},
	},
	tableHeader: {
		display: 'grid',
		gridTemplateColumns: QUOTE_TABLE_COLUMNS,
		gap: 16,
		alignItems: 'center',
		padding: '0 20px 10px',
		borderBottom: '1px solid #e2e0da',
	},
	invoiceTableHeader: {
		display: 'grid',
		gridTemplateColumns: INVOICE_TABLE_COLUMNS,
		gap: 16,
		padding: '0 20px 10px',
		borderBottom: '1px solid #e2e0da',
	},
	customerRow: {
		display: 'grid',
		gridTemplateColumns: '1.5fr 1.2fr 1.2fr auto',
		gap: 14,
		alignItems: 'center',
		padding: '14px 0',
		borderBottom: '1px solid #eceae4',
		'&:last-child': {
			borderBottom: 'none',
		},
	},
	rowTitle: {
		fontSize: 14,
		fontWeight: 500,
		color: '#1a1d1a',
	},
	rowMeta: {
		fontSize: 12,
		color: '#8a918a',
		marginTop: 2,
	},
	columnLabel: {
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		color: '#a4aca4',
	},
	sortButton: {
		padding: 0,
		minWidth: 0,
		justifyContent: 'flex-start',
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		color: '#a4aca4',
		'&:hover': {
			background: 'transparent',
			color: '#505850',
		},
	},
	sortButtonActive: {
		color: '#505850',
	},
	sortArrow: {
		marginLeft: 4,
		fontSize: 12,
		opacity: 0.45,
	},
	sortArrowActive: {
		opacity: 1,
	},
	sentStatus: {
		display: 'inline-block',
		padding: '3px 8px',
		borderRadius: 6,
		fontSize: 12,
		fontWeight: 500,
		whiteSpace: 'nowrap',
		justifySelf: 'start',
	},
	editButton: {
		minWidth: 60,
		minHeight: 30,
		whiteSpace: 'nowrap',
		borderRadius: 7,
		textTransform: 'none',
		fontWeight: 500,
		fontSize: 13,
		borderColor: '#d4d2cc',
		color: '#374038',
	},
	status: {
		height: 24,
		borderRadius: 6,
		fontWeight: 500,
		fontSize: 12,
		color: '#1a1d1a',
	},
	emptyState: {
		padding: '40px 16px',
		textAlign: 'center',
		color: '#909890',
		fontSize: 14,
		background: '#f4f3f0',
		borderRadius: 10,
	},
	error: {
		marginBottom: 14,
		padding: '10px 14px',
		borderRadius: 8,
		background: '#fef2f2',
		color: '#b91c1c',
		fontSize: 13,
		fontWeight: 500,
	},
	previewHero: {
		padding: 24,
		borderRadius: 12,
		background: '#f4f3f0',
		border: '1px solid #e6e4de',
		marginBottom: 18,
	},
	quoteBuilder: {
		marginTop: 16,
		padding: 20,
		borderRadius: 12,
		background: '#ffffff',
		border: '1px solid #e6e4de',
	},
	lineItem: {
		display: 'grid',
		gridTemplateColumns: '1.4fr 0.7fr 0.8fr 0.8fr',
		gap: 12,
		padding: '12px 0',
		borderBottom: '1px solid #eceae4',
	},
	totalBox: {
		marginTop: 16,
		marginLeft: 'auto',
		maxWidth: 280,
		padding: 16,
		borderRadius: 10,
		background: '#1a1d1a',
		color: '#ffffff',
	},
	statGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, 1fr)',
		gap: 14,
		marginBottom: 36,
	},
	statCard: {
		padding: '18px 20px',
		borderRadius: 10,
		background: '#eeecea',
	},
	statLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: 0.8,
		textTransform: 'uppercase',
		color: '#505850',
		marginBottom: 8,
	},
	statValue: {
		fontSize: 28,
		fontWeight: 600,
		letterSpacing: -0.5,
		color: '#1a1d1a',
	},
	statSubtext: {
		fontSize: 13,
		color: '#505850',
		marginTop: 4,
	},
	customerCell: {
		display: 'flex',
		alignItems: 'center',
	},
	closeIconButton: {
		minWidth: 34,
		width: 34,
		height: 34,
		borderRadius: 8,
		padding: 0,
		fontSize: 18,
		fontWeight: 400,
		lineHeight: 1,
		color: '#737873',
		border: '1px solid #e2e0da',
	},
	customerTableHeader: {
		display: 'grid',
		gridTemplateColumns: CUSTOMER_TABLE_COLUMNS,
		gap: 16,
		padding: '0 20px 10px',
		borderBottom: '1px solid #e2e0da',
	},
	customerListRow: {
		display: 'grid',
		gridTemplateColumns: CUSTOMER_TABLE_COLUMNS,
		gap: 16,
		alignItems: 'center',
		padding: '10px 20px',
		borderBottom: '1px solid #eceae4',
		'&:last-child': {
			borderBottom: 'none',
		},
	},
	productTableHeader: {
		display: 'grid',
		gridTemplateColumns: '1.8fr 1fr 1fr 0.9fr auto',
		gap: 16,
		padding: '0 20px 10px',
		borderBottom: '1px solid #e2e0da',
	},
	productListRow: {
		display: 'grid',
		gridTemplateColumns: '1.8fr 1fr 1fr 0.9fr auto',
		gap: 16,
		alignItems: 'center',
		padding: '10px 20px',
		borderBottom: '1px solid #eceae4',
		'&:last-child': {
			borderBottom: 'none',
		},
	},
	iconEditButton: {
		minWidth: 30,
		width: 30,
		height: 30,
		borderRadius: 7,
		padding: 0,
		justifySelf: 'end',
		border: '1px solid #d4d2cc',
		color: '#606860',
		'&:disabled': {
			color: '#c4cac4',
			borderColor: '#e6e4de',
		},
	},
	pdfModalPaper: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		width: 'min(1000px, calc(100vw - 48px))',
		height: 'min(820px, calc(100vh - 48px))',
		borderRadius: 14,
		background: '#ffffff',
		boxShadow: '0 16px 48px rgba(10, 12, 10, 0.18)',
		outline: 'none',
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
	pdfToolbar: {
		minHeight: 54,
		padding: '0 14px 0 20px',
		borderBottom: '1px solid #e2e0da',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 14,
		background: '#f8f7f5',
	},
	pdfActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
	},
	pdfFrame: {
		flex: 1,
		border: 0,
		background: '#eeede8',
	},
	convertButton: {
		minHeight: 42,
		borderRadius: 8,
		padding: '0 22px',
		background: '#1f6f47',
		color: '#ffffff',
		fontSize: 14,
		fontWeight: 500,
		textTransform: 'none',
		boxShadow: 'none',
		'&:hover': {
			background: '#18583a',
			boxShadow: 'none',
		},
		'&:disabled': {
			background: '#d0cec9',
			color: '#8a908a',
		},
	},
	centeredCell: {
		textAlign: 'center',
		justifySelf: 'stretch',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	formModalPaper: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		width: 'min(720px, calc(100vw - 32px))',
		maxHeight: 'calc(100vh - 48px)',
		display: 'flex',
		flexDirection: 'column',
		borderRadius: 14,
		background: '#ffffff',
		boxShadow: '0 16px 48px rgba(10, 12, 10, 0.16)',
		outline: 'none',
		overflow: 'hidden',
	},
	formModalHeader: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: 16,
		padding: '20px 24px 16px',
		borderBottom: '1px solid #eceae4',
	},
	formModalBody: {
		flex: 1,
		overflowY: 'auto',
		padding: '18px 24px 20px',
	},
	formModalFooter: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		padding: '12px 24px',
		borderTop: '1px solid #eceae4',
		background: '#faf9f7',
	},
	formModalActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		'& .MuiButton-root': {
			minHeight: 36,
			padding: '0 16px',
		},
	},
	formSectionLabel: {
		margin: '14px 0 8px',
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.8,
		color: '#8a918a',
	},
	itemsEditorHeader: {
		display: 'grid',
		gridTemplateColumns: ITEM_EDITOR_COLUMNS,
		gap: 8,
		padding: '0 0 6px',
		marginTop: 10,
	},
	itemsEditorRow: {
		display: 'grid',
		gridTemplateColumns: ITEM_EDITOR_COLUMNS,
		gap: 8,
		alignItems: 'center',
		marginBottom: 8,
	},
	lineTotal: {
		fontSize: 13,
		fontWeight: 500,
		color: '#1a1d1a',
		textAlign: 'right',
		fontVariantNumeric: 'tabular-nums',
	},
	removeLineButton: {
		minWidth: 30,
		width: 30,
		height: 30,
		padding: 0,
		borderRadius: 7,
		color: '#8a918a',
		'&:hover': {
			color: '#b3261e',
			background: '#fbeceb',
		},
	},
	addItemButton: {
		minHeight: 30,
		padding: '0 8px',
		marginLeft: -8,
		borderRadius: 7,
		fontSize: 13,
		fontWeight: 500,
		textTransform: 'none',
		color: '#1f6f47',
	},
	totals: {
		width: 300,
		maxWidth: '100%',
		marginLeft: 'auto',
		marginTop: 8,
		paddingTop: 10,
		borderTop: '1px solid #eceae4',
	},
	totalsLine: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		minHeight: 34,
	},
	totalsLabel: {
		fontSize: 13,
		color: '#737873',
	},
	totalsValue: {
		fontSize: 13,
		fontWeight: 500,
		color: '#1a1d1a',
		fontVariantNumeric: 'tabular-nums',
	},
	shippingField: {
		width: 120,
		'& input': {
			textAlign: 'right',
		},
	},
	totalsGrand: {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		marginTop: 6,
		paddingTop: 10,
		borderTop: '1px solid #eceae4',
	},
	totalsGrandLabel: {
		fontSize: 13,
		fontWeight: 600,
		color: '#1a1d1a',
	},
	totalsGrandValue: {
		fontSize: 22,
		fontWeight: 600,
		letterSpacing: -0.4,
		color: '#1a1d1a',
		fontVariantNumeric: 'tabular-nums',
	},
	deleteConfirm: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	deleteConfirmText: {
		marginRight: 4,
		fontSize: 13,
		fontWeight: 500,
		color: '#b3261e',
	},
	dangerButton: {
		minHeight: 34,
		borderRadius: 8,
		fontSize: 13,
		fontWeight: 500,
		textTransform: 'none',
		boxShadow: 'none',
		background: '#b3261e',
		color: '#ffffff',
		'&:hover': {
			background: '#8f1e18',
			boxShadow: 'none',
		},
	},
	dangerTextButton: {
		minHeight: 34,
		padding: '0 10px',
		marginLeft: -10,
		borderRadius: 8,
		fontSize: 13,
		fontWeight: 500,
		textTransform: 'none',
		color: '#b3261e',
		'&:hover': {
			background: '#fbeceb',
		},
	},
	tooltipTarget: {
		display: 'inline-flex',
		justifySelf: 'end',
	},
	hoverNumber: {
		display: 'inline-block',
		cursor: 'default',
		borderBottom: '1px dashed #c4c2bc',
	},
	itemsTooltip: {
		minWidth: 220,
		maxWidth: 340,
		padding: '10px 12px',
		borderRadius: 10,
		background: '#ffffff',
		color: '#1a1d1a',
		border: '1px solid #e2e0da',
		boxShadow: '0 8px 24px rgba(10, 12, 10, 0.12)',
		fontSize: 13,
	},
	itemsTooltipTitle: {
		marginBottom: 6,
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.8,
		color: '#8a918a',
	},
	itemsTooltipRow: {
		display: 'flex',
		justifyContent: 'space-between',
		gap: 16,
		padding: '3px 0',
	},
	itemsTooltipQty: {
		fontWeight: 600,
		fontVariantNumeric: 'tabular-nums',
	},
	orderTableHeader: {
		display: 'grid',
		gridTemplateColumns: ORDER_TABLE_COLUMNS,
		gap: 16,
		alignItems: 'center',
		padding: '0 20px 10px',
		borderBottom: '1px solid #e2e0da',
	},
	orderRow: {
		display: 'grid',
		gridTemplateColumns: ORDER_TABLE_COLUMNS,
		gap: 16,
		alignItems: 'center',
		padding: '10px 20px',
		borderBottom: '1px solid #eceae4',
	},
	pickTableHeader: {
		display: 'grid',
		gridTemplateColumns: PICK_TABLE_COLUMNS,
		gap: 16,
		alignItems: 'center',
		padding: '0 20px 10px',
		borderBottom: '1px solid #e2e0da',
	},
	pickRow: {
		display: 'grid',
		gridTemplateColumns: PICK_TABLE_COLUMNS,
		gap: 16,
		alignItems: 'center',
		padding: '10px 20px',
		borderBottom: '1px solid #eceae4',
	},
	statusCell: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 6,
	},
	adjustedTag: {
		padding: '2px 6px',
		borderRadius: 5,
		border: '1px solid #e8c77d',
		color: '#8a5a00',
		fontSize: 11,
		fontWeight: 500,
	},
	tabCount: {
		marginLeft: 6,
		padding: '1px 6px',
		borderRadius: 8,
		background: '#fff0d7',
		color: '#8a5a00',
		fontSize: 11,
		fontWeight: 600,
	},
	receivedRow: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 16,
		margin: '2px 0 6px',
	},
	receivedDate: {
		width: 170,
		marginLeft: 'auto',
	},
	pickTitleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	pickItemsHeader: {
		display: 'grid',
		gridTemplateColumns: PICK_ITEM_COLUMNS,
		gap: 8,
		padding: '0 8px 6px',
	},
	pickItemRow: {
		display: 'grid',
		gridTemplateColumns: PICK_ITEM_COLUMNS,
		gap: 8,
		alignItems: 'center',
		padding: '5px 8px',
		borderRadius: 8,
	},
	pickItemChanged: {
		background: '#fff7e6',
	},
	changedField: {
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: '#e0a73a',
		},
		'& input': {
			fontWeight: 600,
		},
	},
	pickSummary: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 24,
		marginTop: 12,
	},
	pickNotes: {
		flex: 1,
		paddingTop: 10,
	},
	footerNote: {
		fontSize: 13,
		color: '#737873',
	},
	nameLink: {
		minWidth: 0,
		padding: '2px 6px',
		marginLeft: -6,
		borderRadius: 6,
		justifyContent: 'flex-start',
		textAlign: 'left',
		fontSize: 14,
		fontWeight: 500,
		lineHeight: 1.4,
		letterSpacing: 0,
		textTransform: 'none',
		color: '#1a1d1a',
		'&:hover': {
			background: '#eeecea',
			textDecoration: 'underline',
			textUnderlineOffset: '3px',
		},
	},
	detailGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: 16,
		marginBottom: 18,
		paddingBottom: 16,
		borderBottom: '1px solid #eceae4',
	},
	detailField: {
		minWidth: 0,
	},
	detailLabel: {
		marginBottom: 3,
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: 0.6,
		color: '#8a918a',
	},
	detailValue: {
		fontSize: 13,
		color: '#1a1d1a',
	},
	detailItemsHeader: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) 80px 100px 100px',
		gap: 12,
		padding: '0 0 6px',
		borderBottom: '1px solid #e2e0da',
	},
	detailItemRow: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) 80px 100px 100px',
		gap: 12,
		alignItems: 'center',
		padding: '9px 0',
		borderBottom: '1px solid #f0eee9',
	},
	detailNotes: {
		fontSize: 13,
		color: '#374038',
		whiteSpace: 'pre-wrap',
	},
	filesSection: {
		marginBottom: 6,
	},
	fileList: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
		marginBottom: 8,
	},
	fileChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		maxWidth: '100%',
		padding: '4px 4px 4px 8px',
		borderRadius: 8,
		border: '1px solid #e2e0da',
		background: '#ffffff',
		fontSize: 13,
	},
	fileChipPending: {
		borderStyle: 'dashed',
		background: '#faf9f7',
	},
	fileLink: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maxWidth: 260,
		color: '#1a1d1a',
		fontWeight: 500,
		textDecoration: 'none',
		'a&:hover': {
			textDecoration: 'underline',
		},
	},
	fileSize: {
		fontSize: 12,
		color: '#8a918a',
		whiteSpace: 'nowrap',
	},
	fileRemove: {
		minWidth: 24,
		width: 24,
		height: 24,
		padding: 0,
		borderRadius: 6,
		color: '#8a918a',
		'&:hover': {
			color: '#b3261e',
			background: '#fbeceb',
		},
	},
	dropZone: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
		padding: '10px 12px',
		borderRadius: 8,
		border: '1px dashed #cfccc4',
		background: '#faf9f7',
		color: '#505850',
		fontSize: 13,
		cursor: 'pointer',
		'&:hover': {
			borderColor: '#8a918a',
		},
	},
	dropZoneActive: {
		borderColor: '#1f6f47',
		background: '#eef7f1',
	},
	dropZoneHint: {
		marginLeft: 'auto',
		fontSize: 12,
		color: '#8a918a',
	},
	hiddenInput: {
		display: 'none',
	},
	clipIcon: {
		fontSize: 15,
		color: '#8a918a',
		transform: 'rotate(45deg)',
	},
});

function StatusChip({ value }) {
	return (
		<Chip
			label={value}
			style={{
				backgroundColor: statusColor[value] || '#edf1f7',
				height: 24,
				borderRadius: 6,
				fontSize: 12,
				fontWeight: 500,
				color: '#1a1d1a',
			}}
		/>
	);
}

const formatCurrency = (value) =>
	Number(value || 0).toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
	});

const formatDate = (value) => {
	if (!value) {
		return 'Not set';
	}
	return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const quoteDisplayNumber = (quote) =>
	quote.quote_number || `QT-${quote.id.toString().padStart(4, '0')}`;

const wholesaleTabs = ['quotes', 'orders', 'picklists', 'invoices', 'customers', 'products'];
const currentWholesaleYear = new Date().getFullYear();
const wholesaleYearOptions = [0, 1, 2, 3, 4].map((offset) => currentWholesaleYear - offset);

const tabFromHash = () => {
	if (typeof window === 'undefined') {
		return 0;
	}

	const hashTab = window.location.hash.replace('#', '').toLowerCase();
	const tabIndex = wholesaleTabs.indexOf(hashTab);
	return tabIndex >= 0 ? tabIndex : 0;
};

function WholesalePage({ classes }) {
	const [tab, setTab] = useState(tabFromHash);
	const [customers, setCustomers] = useState([]);
	const [products, setProducts] = useState([]);
	const [quotes, setQuotes] = useState([]);
	const [invoices, setInvoices] = useState([]);
	const [form, setForm] = useState(emptyCustomer);
	const [productForm, setProductForm] = useState(emptyProduct);
	const [quoteForm, setQuoteForm] = useState(emptyQuote);
	const [editingId, setEditingId] = useState(null);
	const [editingProductId, setEditingProductId] = useState(null);
	const [editingQuoteId, setEditingQuoteId] = useState(null);
	const [customerModalOpen, setCustomerModalOpen] = useState(false);
	const [productModalOpen, setProductModalOpen] = useState(false);
	const [quoteModalOpen, setQuoteModalOpen] = useState(false);
	const [pdfQuote, setPdfQuote] = useState(null);
	const [pdfInvoice, setPdfInvoice] = useState(null);
	const [quoteToDelete, setQuoteToDelete] = useState(null);
	const [viewQuote, setViewQuote] = useState(null);
	const [orders, setOrders] = useState([]);
	const [orderForm, setOrderForm] = useState(emptyOrder);
	const [editingOrderId, setEditingOrderId] = useState(null);
	const [orderModalOpen, setOrderModalOpen] = useState(false);
	const [orderToDelete, setOrderToDelete] = useState(null);
	const [pickForm, setPickForm] = useState(null);
	const [savingOrder, setSavingOrder] = useState(false);
	const [orderError, setOrderError] = useState('');
	const [pendingFiles, setPendingFiles] = useState([]);
	const [uploadingFiles, setUploadingFiles] = useState(false);
	const [dragActive, setDragActive] = useState(false);
	const tabsActions = useRef(null);
	const [quoteSort, setQuoteSort] = useState({ field: 'created', direction: 'desc' });
	const [wholesaleYear, setWholesaleYear] = useState(currentWholesaleYear);
	const [saving, setSaving] = useState(false);
	const [savingProduct, setSavingProduct] = useState(false);
	const [savingQuote, setSavingQuote] = useState(false);
	const [error, setError] = useState('');
	const [productError, setProductError] = useState('');
	const [quoteError, setQuoteError] = useState('');
	const [invoiceError, setInvoiceError] = useState('');

	const loadCustomers = async () => {
		try {
			const { data } = await axios.get('/api/wholesale/customers');
			setCustomers(Array.isArray(data) ? data : []);
		} catch (err) {
			setCustomers([]);
			setError(err.response?.data?.message || 'Unable to load customers.');
		}
	};

	const loadProducts = async () => {
		try {
			const { data } = await axios.get('/api/wholesale/products');
			setProducts(Array.isArray(data) ? data : []);
		} catch (err) {
			setProducts([]);
			setProductError(err.response?.data?.message || 'Unable to load products.');
		}
	};

	const loadQuotes = async (year = wholesaleYear) => {
		try {
			const { data } = await axios.get('/api/wholesale/quotes', { params: { year } });
			setQuotes(Array.isArray(data) ? data : []);
		} catch (err) {
			setQuotes([]);
			setQuoteError(err.response?.data?.message || 'Unable to load quotes.');
		}
	};

	const loadOrders = async (year = wholesaleYear) => {
		try {
			const { data } = await axios.get('/api/wholesale/orders', { params: { year } });
			setOrders(Array.isArray(data) ? data : []);
		} catch (err) {
			setOrders([]);
			setOrderError(err.response?.data?.message || 'Unable to load orders.');
		}
	};

	const loadInvoices = async (year = wholesaleYear) => {
		try {
			const { data } = await axios.get('/api/wholesale/invoices', { params: { year } });
			setInvoices(Array.isArray(data) ? data : []);
		} catch (err) {
			setInvoices([]);
			setInvoiceError(err.response?.data?.message || 'Unable to load invoices.');
		}
	};

	useEffect(() => {
		loadCustomers();
		loadProducts();
	}, []);

	useEffect(() => {
		loadQuotes(wholesaleYear);
		loadOrders(wholesaleYear);
		loadInvoices(wholesaleYear);
	}, [wholesaleYear]);

	const isArchivedYear = wholesaleYear !== currentWholesaleYear;
	const openPickCount = orders.filter((order) => order.status === 'Picking' || order.status === 'Packed').length;

	// Tab widths change when the pick-list count badge appears and when the webfont
	// finishes loading, so the underline has to re-measure.
	useEffect(() => {
		const updateIndicator = () => tabsActions.current && tabsActions.current.updateIndicator();
		updateIndicator();
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(updateIndicator);
		}
	}, [openPickCount, tab]);

	useEffect(() => {
		if (!window.location.hash) {
			window.history.replaceState(null, '', `${window.location.pathname}#${wholesaleTabs[tab]}`);
		}

		const handleHashChange = () => {
			setTab(tabFromHash());
		};

		window.addEventListener('hashchange', handleHashChange);
		return () => window.removeEventListener('hashchange', handleHashChange);
	}, []);

	const changeTab = (nextTab) => {
		setTab(nextTab);
		window.history.replaceState(null, '', `${window.location.pathname}#${wholesaleTabs[nextTab]}`);
	};

	// The nav bar's Wholesale link lands on /wholesale with no tab; treat that as home,
	// even when the page is already open on another tab.
	const location = useLocation();
	useEffect(() => {
		if (!location.hash) {
			changeTab(0);
		}
	}, [location.key]);

	const updateForm = (field) => (event) => {
		setForm({ ...form, [field]: event.target.value });
		if (error) {
			setError('');
		}
	};

	const updateLocation = (index, field) => (event) => {
		setForm({
			...form,
			locations: form.locations.map((location, locationIndex) =>
				locationIndex === index ? { ...location, [field]: event.target.value } : location,
			),
		});
	};

	const addLocation = () => {
		if (form.locations.length >= 4) {
			return;
		}

		setForm({
			...form,
			locations: [
				...form.locations,
				{
					address_1: '',
					address_2: '',
					city: '',
					state: '',
					zip: '',
				},
			],
		});
	};

	const removeLocation = (index) => {
		if (index === 0) {
			return;
		}

		setForm({
			...form,
			locations: form.locations.filter((_, locationIndex) => locationIndex !== index),
		});
	};

	const resetForm = () => {
		setForm(emptyCustomer);
		setEditingId(null);
		setError('');
	};

	const openNewCustomer = () => {
		resetForm();
		setCustomerModalOpen(true);
	};

	const closeCustomerModal = () => {
		setCustomerModalOpen(false);
		resetForm();
	};

	const resetProductForm = () => {
		setProductForm(emptyProduct);
		setEditingProductId(null);
		setProductError('');
	};

	const openNewProduct = () => {
		resetProductForm();
		setProductModalOpen(true);
	};

	const closeProductModal = () => {
		setProductModalOpen(false);
		resetProductForm();
	};

	const resetQuoteForm = () => {
		setQuoteForm(emptyQuote);
		setEditingQuoteId(null);
		setQuoteError('');
	};

	const openNewQuote = () => {
		resetQuoteForm();
		setQuoteModalOpen(true);
	};

	const closeQuoteModal = () => {
		setQuoteModalOpen(false);
		setQuoteToDelete(null);
		resetQuoteForm();
	};

	const openQuotePdf = (quote) => {
		setPdfQuote(quote);
	};

	const closeQuotePdf = () => {
		setPdfQuote(null);
	};

	const openInvoicePdf = (invoice) => {
		setPdfInvoice(invoice);
	};

	const closeInvoicePdf = () => {
		setPdfInvoice(null);
	};

	const openDeleteQuote = (quote) => {
		setQuoteToDelete(quote);
		setQuoteError('');
	};

	const closeDeleteQuote = () => {
		setQuoteToDelete(null);
	};

	const deleteQuote = async () => {
		if (!quoteToDelete) {
			return;
		}

		setSavingQuote(true);
		try {
			await axios.delete(`/api/wholesale/quotes/${quoteToDelete.id}`);
			await loadQuotes();
			await loadInvoices();
			closeQuoteModal();
		} catch (err) {
			setQuoteError(err.response?.data?.message || 'Unable to delete quote.');
		}
		setSavingQuote(false);
	};

	const sortQuotesBy = (field) => {
		setQuoteSort((currentSort) => {
			if (currentSort.field === field) {
				return { field, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' };
			}
			// Names read best A to Z; amounts and dates start with the largest/newest.
			return { field, direction: field === 'customer' ? 'asc' : 'desc' };
		});
	};

	const renderSortHeader = (field, label) => {
		const isActive = quoteSort.field === field;
		const arrow = !isActive ? '↕' : quoteSort.direction === 'asc' ? '↑' : '↓';
		return (
			<Button
				className={`${classes.sortButton} ${isActive ? classes.sortButtonActive : ''}`}
				onClick={() => sortQuotesBy(field)}
			>
				{label}
				<span className={`${classes.sortArrow} ${isActive ? classes.sortArrowActive : ''}`}>{arrow}</span>
			</Button>
		);
	};

	const itemsSubtotal = (items) =>
		items.reduce((total, item) => total + Number(item.amount || 0) * Number(item.unit_price || 0), 0);

	const orderItemFromSaved = (item) => ({
		product_id: item.product_id ? item.product_id.toString() : '',
		amount: item.ordered_qty ?? '',
		unit_price: item.unit_price ?? '',
		pick_qty: item.pick_qty,
		picked_qty: item.picked_qty,
	});

	// Quote lines become the order's product list at quote prices; the customer's
	// quantities get typed in, and lines left blank are dropped on save.
	const orderItemsFromQuote = (quoteItems) =>
		quoteItems.map((item) => ({
			product_id: item.product_id ? item.product_id.toString() : '',
			amount: '',
			unit_price: item.unit_price ?? '',
		}));

	const resetOrderModal = () => {
		setOrderModalOpen(false);
		setEditingOrderId(null);
		setOrderToDelete(null);
		setOrderError('');
		setPendingFiles([]);
		setDragActive(false);
	};

	const openNewOrder = (quote) => {
		resetOrderModal();
		setOrderForm(
			quote
				? {
						...emptyOrder,
						received_date: todayIso(),
						customer_id: quote.customer_id ? quote.customer_id.toString() : '',
						customer_location_id: quote.customer_location_id ? quote.customer_location_id.toString() : '',
						quote_id: quote.id,
						quote_number: quoteDisplayNumber(quote),
						payment_terms: quote.payment_terms || 'net15',
						items: quote.items?.length ? orderItemsFromQuote(quote.items) : emptyOrder.items,
				  }
				: { ...emptyOrder, received_date: todayIso() },
		);
		setOrderModalOpen(true);
	};

	const editOrder = (order) => {
		resetOrderModal();
		setEditingOrderId(order.id);
		setOrderForm({
			customer_id: order.customer_id ? order.customer_id.toString() : '',
			customer_location_id: order.customer_location_id ? order.customer_location_id.toString() : '',
			quote_id: order.quote_id || '',
			quote_number: order.quote_number || '',
			received_via: order.received_via || 'email',
			received_date: order.received_date ? order.received_date.substring(0, 10) : todayIso(),
			expected_delivery_date: order.expected_delivery_date ? order.expected_delivery_date.substring(0, 10) : '',
			payment_terms: order.payment_terms || 'net15',
			shipping_handling: order.shipping_handling || '',
			notes: order.notes || '',
			items: order.items.length > 0 ? order.items.map(orderItemFromSaved) : emptyOrder.items,
		});
		setOrderModalOpen(true);
	};

	const updateOrderCustomer = async (customerId) => {
		const customer = customers.find((currentCustomer) => currentCustomer.id.toString() === customerId);
		setOrderForm((currentForm) => ({
			...currentForm,
			customer_id: customerId,
			customer_location_id: customer?.locations?.[0]?.id ? customer.locations[0].id.toString() : '',
			quote_id: '',
			quote_number: '',
		}));
		if (orderError) {
			setOrderError('');
		}
		if (!customerId) {
			return;
		}

		try {
			const { data } = await axios.get(`/api/wholesale/customers/${customerId}/last-quote`);
			if (!data || data.year !== currentWholesaleYear) {
				return;
			}
			setOrderForm((currentForm) => {
				const hasQuantities = currentForm.items.some((item) => Number(item.amount) > 0);
				return {
					...currentForm,
					quote_id: data.id,
					quote_number: data.quote_number,
					payment_terms: data.payment_terms || currentForm.payment_terms,
					items: hasQuantities || data.items.length === 0 ? currentForm.items : orderItemsFromQuote(data.items),
				};
			});
		} catch (err) {
			// Quote pricing is a convenience; the order can still be entered by hand.
		}
	};

	const updateOrderItem = (index, field, value) => {
		setOrderForm({
			...orderForm,
			items: orderForm.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
		});
	};

	const updateOrderProduct = (index, productId) => {
		const product = products.find((currentProduct) => currentProduct.id.toString() === productId);
		setOrderForm({
			...orderForm,
			items: orderForm.items.map((item, itemIndex) =>
				itemIndex === index
					? { ...item, product_id: productId, unit_price: product ? product.price : item.unit_price }
					: item,
			),
		});
	};

	const addOrderItem = () => {
		setOrderForm({ ...orderForm, items: [...orderForm.items, { product_id: '', amount: '', unit_price: '' }] });
	};

	const removeOrderItem = (index) => {
		if (orderForm.items.length === 1) {
			return;
		}
		setOrderForm({ ...orderForm, items: orderForm.items.filter((_, itemIndex) => itemIndex !== index) });
	};

	const orderPayload = (form) => ({
		customer_id: form.customer_id,
		customer_location_id: form.customer_location_id || '',
		quote_id: form.quote_id || '',
		received_via: form.received_via,
		received_date: form.received_date,
		expected_delivery_date: form.expected_delivery_date,
		payment_terms: form.payment_terms,
		shipping_handling: form.shipping_handling || 0,
		notes: form.notes,
		items: form.items.map((item) => ({
			product_id: item.product_id,
			ordered_qty: item.amount,
			unit_price: item.unit_price,
			pick_qty: item.pick_qty,
			picked_qty: item.picked_qty,
		})),
	});

	const pickFormFromOrder = (order) => ({
		...order,
		received_date: order.received_date ? order.received_date.substring(0, 10) : '',
		expected_delivery_date: order.expected_delivery_date ? order.expected_delivery_date.substring(0, 10) : '',
		shipping_handling: order.shipping_handling || '',
		notes: order.notes || '',
		items: order.items.map((item) => ({
			product_id: item.product_id ? item.product_id.toString() : '',
			product_name: item.product_name,
			ordered_qty: item.ordered_qty,
			pick_qty: item.pick_qty ?? '',
			picked_qty: item.picked_qty ?? '',
			unit_price: item.unit_price ?? '',
		})),
	});

	const openPickList = async (order) => {
		setOrderError('');
		if (order.status !== 'Received') {
			setPickForm(pickFormFromOrder(order));
			return;
		}
		try {
			const { data } = await axios.post(`/api/wholesale/orders/${order.id}/status`, { status: 'Picking' });
			await loadOrders();
			setPickForm(pickFormFromOrder(data));
		} catch (err) {
			setOrderError(err.response?.data?.message || 'Unable to start the pick list.');
		}
	};

	const saveOrder = async (event, { sendToPicking = false } = {}) => {
		if (event) {
			event.preventDefault();
		}
		if (!orderForm.customer_id) {
			setOrderError('Customer is required.');
			return;
		}

		setSavingOrder(true);
		try {
			const { data } = editingOrderId
				? await axios.put(`/api/wholesale/orders/${editingOrderId}`, orderPayload(orderForm))
				: await axios.post('/api/wholesale/orders', orderPayload(orderForm));
			if (pendingFiles.length > 0) {
				await uploadFiles(data.id, pendingFiles);
			}
			await loadOrders();
			resetOrderModal();
			if (sendToPicking) {
				await openPickList(data);
			}
		} catch (err) {
			setOrderError(err.response?.data?.message || 'Unable to save order.');
		}
		setSavingOrder(false);
	};

	const deleteOrder = async () => {
		if (!orderToDelete) {
			return;
		}
		setSavingOrder(true);
		try {
			await axios.delete(`/api/wholesale/orders/${orderToDelete.id}`);
			await loadOrders();
			resetOrderModal();
		} catch (err) {
			setOrderError(err.response?.data?.message || 'Unable to delete order.');
		}
		setSavingOrder(false);
	};

	const updatePickItem = (index, field, value) => {
		setPickForm({
			...pickForm,
			items: pickForm.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
		});
	};

	const updatePickProduct = (index, productId) => {
		const product = products.find((currentProduct) => currentProduct.id.toString() === productId);
		setPickForm({
			...pickForm,
			items: pickForm.items.map((item, itemIndex) =>
				itemIndex === index
					? {
							...item,
							product_id: productId,
							product_name: product ? product.item_name : '',
							unit_price: product ? product.price : item.unit_price,
					  }
					: item,
			),
		});
	};

	const addPickItem = () => {
		setPickForm({
			...pickForm,
			items: [
				...pickForm.items,
				{ product_id: '', product_name: '', ordered_qty: 0, pick_qty: '', picked_qty: '', unit_price: '', isNew: true },
			],
		});
	};

	const savePickList = async () => {
		const { data } = await axios.put(`/api/wholesale/orders/${pickForm.id}`, {
			...orderPayload({ ...pickForm, items: [] }),
			items: pickForm.items.map((item) => ({
				product_id: item.product_id,
				ordered_qty: item.ordered_qty,
				pick_qty: item.pick_qty,
				picked_qty: item.picked_qty,
				unit_price: item.unit_price,
			})),
		});
		return data;
	};

	const runPickAction = async (action) => {
		setSavingOrder(true);
		setOrderError('');
		try {
			const saved = await savePickList();
			if (action === 'packed' || action === 'picking') {
				const { data } = await axios.post(`/api/wholesale/orders/${saved.id}/status`, {
					status: action === 'packed' ? 'Packed' : 'Picking',
				});
				setPickForm(pickFormFromOrder(data));
			} else if (action === 'invoice') {
				await axios.post(`/api/wholesale/orders/${saved.id}/invoices`);
				await loadInvoices();
				setPickForm(null);
				changeTab(3);
			} else {
				setPickForm(null);
			}
			await loadOrders();
		} catch (err) {
			setOrderError(err.response?.data?.message || 'Unable to save the pick list.');
		}
		setSavingOrder(false);
	};

	const emailQuote = async () => {
		if (!pdfQuote) {
			return;
		}

		try {
			await axios.post(`/api/wholesale/quotes/${pdfQuote.id}/email`);
			await loadQuotes();
		} catch (err) {
			setQuoteError(err.response?.data?.message || 'Email sending is not configured yet.');
		}
	};

	const emailInvoice = async () => {
		if (!pdfInvoice) {
			return;
		}

		try {
			await axios.post(`/api/wholesale/invoices/${pdfInvoice.id}/email`);
		} catch (err) {
			setInvoiceError(err.response?.data?.message || 'Email sending is not configured yet.');
		}
	};

	const updateQuoteItem = (index, field, value) => {
		setQuoteForm({
			...quoteForm,
			items: quoteForm.items.map((item, itemIndex) =>
				itemIndex === index ? { ...item, [field]: value } : item,
			),
		});
		if (quoteError) {
			setQuoteError('');
		}
	};

	const updateQuoteProduct = (index, productId) => {
		const product = products.find((currentProduct) => currentProduct.id.toString() === productId);
		setQuoteForm({
			...quoteForm,
			items: quoteForm.items.map((item, itemIndex) =>
				itemIndex === index
					? { ...item, product_id: productId, unit_price: product ? product.price : item.unit_price }
					: item,
			),
		});
		if (quoteError) {
			setQuoteError('');
		}
	};

	const addQuoteItem = () => {
		setQuoteForm({
			...quoteForm,
			items: [...quoteForm.items, { product_id: '', amount: '', unit_price: '' }],
		});
	};

	const removeQuoteItem = (index) => {
		if (quoteForm.items.length === 1) {
			return;
		}

		setQuoteForm({
			...quoteForm,
			items: quoteForm.items.filter((_, itemIndex) => itemIndex !== index),
		});
	};

	const updateQuoteCustomer = async (customerId) => {
		const customer = customers.find((currentCustomer) => currentCustomer.id.toString() === customerId);
		setQuoteForm({
			...quoteForm,
			customer_id: customerId,
			customer_location_id: customer?.locations?.[0]?.id ? customer.locations[0].id.toString() : '',
		});
		if (quoteError) {
			setQuoteError('');
		}

		try {
			const { data } = await axios.get(`/api/wholesale/customers/${customerId}/last-quote`);
			if (data && Array.isArray(data.items) && data.items.length > 0) {
				setQuoteForm((currentForm) => ({ ...currentForm, items: data.items }));
			}
		} catch (err) {
			// Prefill is a convenience only; ignore failures.
		}
	};

	const editProduct = (product) => {
		setEditingProductId(product.id);
		setProductForm({
			item_code: product.item_code || '',
			item_name: product.item_name || '',
			upc_code: product.upc_code || '',
			price: product.price || '',
			unit_weight: product.unit_weight || '',
		});
		setProductError('');
		setProductModalOpen(true);
	};

	const editQuote = (quote) => {
		setEditingQuoteId(quote.id);
		setQuoteForm({
			customer_id: quote.customer_id ? quote.customer_id.toString() : '',
			customer_location_id: quote.customer_location_id ? quote.customer_location_id.toString() : '',
			items:
				quote.items && quote.items.length > 0
					? quote.items.map((quoteItem) => ({
							product_id: quoteItem.product_id ? quoteItem.product_id.toString() : '',
							amount: quoteItem.amount || '',
							unit_price: quoteItem.unit_price || '',
					  }))
					: emptyQuote.items,
			shipping_handling: quote.shipping_handling || '',
			payment_terms: quote.payment_terms || 'net15',
			expected_delivery_date: quote.expected_delivery_date
				? quote.expected_delivery_date.substring(0, 10)
				: '',
			notes: quote.notes || '',
		});
		setQuoteError('');
		setQuoteModalOpen(true);
	};

	const editCustomer = (customer) => {
		const hasBillingAddress = Boolean(
			customer.billing_address_1 ||
				customer.billing_address_2 ||
				customer.billing_city ||
				customer.billing_state ||
				customer.billing_zip,
		);
		setEditingId(customer.id);
		setForm({
			first_name: customer.first_name || '',
			last_name: customer.last_name || '',
			company_name: customer.company_name || customer.business_name || '',
			contact_name: customer.contact_name || '',
			phone: customer.phone || '',
			fax: customer.fax || '',
			email: customer.email || '',
			accounts_payable_email: customer.accounts_payable_email || '',
			quote_delivery_method: customer.quote_delivery_method || 'email',
			invoice_delivery_method: customer.invoice_delivery_method || 'email',
			billing_address_1: customer.billing_address_1 || '',
			billing_address_2: customer.billing_address_2 || '',
			billing_city: customer.billing_city || '',
			billing_state: customer.billing_state || '',
			billing_zip: customer.billing_zip || '',
			notes: customer.notes || '',
			locations:
				customer.locations && customer.locations.length > 0
					? customer.locations.map((location) => ({
							address_1: location.address_1 || '',
							address_2: location.address_2 || '',
							city: location.city || '',
							state: location.state || '',
							zip: location.zip || '',
					  }))
					: emptyCustomer.locations,
			billing_same_as_default_address: !hasBillingAddress,
		});
		setError('');
		setCustomerModalOpen(true);
	};

	const saveCustomer = async (event) => {
		event.preventDefault();
		if (!form.company_name.trim()) {
			setError('Company name is required.');
			return;
		}

		setSaving(true);
		try {
			const customerPayload = form.billing_same_as_default_address
				? {
						...form,
						billing_address_1: '',
						billing_address_2: '',
						billing_city: '',
						billing_state: '',
						billing_zip: '',
				  }
				: form;

			if (editingId) {
				await axios.put(`/api/wholesale/customers/${editingId}`, customerPayload);
			} else {
				await axios.post('/api/wholesale/customers', customerPayload);
			}
			await loadCustomers();
			resetForm();
			setCustomerModalOpen(false);
		} catch (err) {
			setError(err.response?.data?.message || 'Unable to save customer.');
		}
		setSaving(false);
	};

	const saveProduct = async (event) => {
		event.preventDefault();
		if (!productForm.item_name.trim()) {
			setProductError('Item name is required.');
			return;
		}

		setSavingProduct(true);
		try {
			if (editingProductId) {
				await axios.put(`/api/wholesale/products/${editingProductId}`, productForm);
			} else {
				await axios.post('/api/wholesale/products', productForm);
			}
			await loadProducts();
			resetProductForm();
			setProductModalOpen(false);
		} catch (err) {
			setProductError(err.response?.data?.message || 'Unable to save product.');
		}
		setSavingProduct(false);
	};

	const saveQuote = async (event) => {
		event.preventDefault();
		if (!quoteForm.customer_id) {
			setQuoteError('Customer is required.');
			return;
		}
		if (!quoteForm.items.some((item) => item.product_id)) {
			setQuoteError('At least one product is required.');
			return;
		}

		setSavingQuote(true);
		try {
			if (editingQuoteId) {
				await axios.put(`/api/wholesale/quotes/${editingQuoteId}`, quoteForm);
			} else {
				await axios.post('/api/wholesale/quotes', quoteForm);
			}
			await loadQuotes();
			await loadInvoices();
			closeQuoteModal();
		} catch (err) {
			setQuoteError(err.response?.data?.message || 'Unable to save quote.');
		}
		setSavingQuote(false);
	};

	const renderDeliveryToggle = (field) => (
		<div className={classes.segmented} role='group'>
			{deliveryOptions.map((option) => (
				<Button
					key={option}
					className={`${classes.segmentButton} ${form[field] === option ? classes.segmentButtonActive : ''}`}
					onClick={() => setForm({ ...form, [field]: option })}
					aria-pressed={form[field] === option}
				>
					{option}
				</Button>
			))}
		</div>
	);

	const renderAddressFields = ({ values, onChange }) => (
		<React.Fragment>
			<div className={classes.fieldRow}>
				<TextField className={classes.compactField} label='Address 1' value={values.address_1} onChange={onChange('address_1')} variant='outlined' size='small' fullWidth />
				<TextField className={classes.compactField} label='Address 2' value={values.address_2} onChange={onChange('address_2')} variant='outlined' size='small' fullWidth />
			</div>
			<div className={classes.addressCityRow}>
				<TextField className={classes.compactField} label='City' value={values.city} onChange={onChange('city')} variant='outlined' size='small' fullWidth />
				<TextField className={classes.compactField} label='State' value={values.state} onChange={onChange('state')} variant='outlined' size='small' fullWidth />
				<TextField className={classes.compactField} label='Zip' value={values.zip} onChange={onChange('zip')} variant='outlined' size='small' fullWidth />
			</div>
		</React.Fragment>
	);

	const billingValues = {
		address_1: form.billing_address_1,
		address_2: form.billing_address_2,
		city: form.billing_city,
		state: form.billing_state,
		zip: form.billing_zip,
	};

	const renderCustomerForm = () => (
		<form onSubmit={saveCustomer} className={classes.formModalPaper}>
			<div className={classes.formModalHeader}>
				<div>
					<Typography className={classes.sectionTitle}>{editingId ? 'Edit customer' : 'New customer'}</Typography>
					{editingId && (
						<Typography className={classes.sectionHint}>
							{pluralize(getCustomerQuoteCount(editingId), 'quote')} ·{' '}
							{pluralize(getCustomerInvoiceCount(editingId), 'open invoice')}
						</Typography>
					)}
				</div>
				<Button variant='text' className={classes.closeIconButton} onClick={closeCustomerModal} aria-label='Close'>
					×
				</Button>
			</div>
			<div className={classes.formModalBody}>
				{error && <div className={classes.error}>{error}</div>}
				<TextField className={classes.compactField} label='Company name' value={form.company_name} onChange={updateForm('company_name')} variant='outlined' size='small' fullWidth required />
				<div className={classes.fieldRow}>
					<TextField className={classes.compactField} label='First name' value={form.first_name} onChange={updateForm('first_name')} variant='outlined' size='small' fullWidth />
					<TextField className={classes.compactField} label='Last name' value={form.last_name} onChange={updateForm('last_name')} variant='outlined' size='small' fullWidth />
				</div>
				<div className={classes.fieldRow}>
					<TextField className={classes.compactField} label='Phone' value={form.phone} onChange={updateForm('phone')} variant='outlined' size='small' fullWidth />
					<TextField className={classes.compactField} label='Fax' value={form.fax} onChange={updateForm('fax')} variant='outlined' size='small' fullWidth />
				</div>
				<div className={classes.fieldRow}>
					<TextField className={classes.compactField} label='Email' value={form.email} onChange={updateForm('email')} variant='outlined' size='small' fullWidth />
					<TextField className={classes.compactField} label='Accounts payable email' value={form.accounts_payable_email} onChange={updateForm('accounts_payable_email')} variant='outlined' size='small' fullWidth />
				</div>

				<Typography className={classes.formSectionLabel}>Send by</Typography>
				<div className={classes.deliveryRow}>
					<Typography className={classes.deliveryLabel}>Quotes</Typography>
					{renderDeliveryToggle('quote_delivery_method')}
				</div>
				<div className={classes.deliveryRow}>
					<Typography className={classes.deliveryLabel}>Invoices</Typography>
					{renderDeliveryToggle('invoice_delivery_method')}
				</div>

				<Typography className={classes.formSectionLabel}>Locations</Typography>
				{form.locations.map((location, index) => (
					<div key={index} className={classes.locationCard}>
						<div className={classes.locationHeader}>
							<Typography className={classes.locationTitle}>
								Location {index + 1}
								{index === 0 && <span className={classes.defaultBadge}>Default</span>}
							</Typography>
							{index > 0 && (
								<Button className={classes.dangerTextButton} onClick={() => removeLocation(index)}>
									Remove
								</Button>
							)}
						</div>
						{renderAddressFields({ values: location, onChange: (field) => updateLocation(index, field) })}
					</div>
				))}
				{form.locations.length < 4 && (
					<Button className={classes.addItemButton} onClick={addLocation}>
						+ Add location
					</Button>
				)}

				<Typography className={classes.formSectionLabel}>Billing address</Typography>
				<FormControlLabel
					className={classes.checkboxLabel}
					control={
						<Checkbox
							size='small'
							checked={form.billing_same_as_default_address}
							onChange={(event) =>
								setForm({ ...form, billing_same_as_default_address: event.target.checked })
							}
							color='default'
						/>
					}
					label='Same as default location'
				/>
				{!form.billing_same_as_default_address && (
					<div style={{ marginTop: 8 }}>
						{renderAddressFields({
							values: billingValues,
							onChange: (field) => updateForm(`billing_${field}`),
						})}
					</div>
				)}

				<Typography className={classes.formSectionLabel}>Notes</Typography>
				<TextField value={form.notes} onChange={updateForm('notes')} variant='outlined' size='small' fullWidth multiline minRows={2} inputProps={{ 'aria-label': 'Notes' }} />
			</div>
			<div className={classes.formModalFooter}>
				<span />
				<div className={classes.formModalActions}>
					<Button variant='outlined' className={classes.secondaryButton} onClick={closeCustomerModal}>
						Cancel
					</Button>
					<Button type='submit' variant='contained' disabled={saving} className={classes.primaryButton}>
						{saving ? 'Saving...' : editingId ? 'Save customer' : 'Add customer'}
					</Button>
				</div>
			</div>
		</form>
	);

	const renderCustomers = () => (
		<React.Fragment>
			<div>
				<div className={classes.customerTableHeader}>
					<Typography className={classes.columnLabel}>Company</Typography>
					<Typography className={`${classes.columnLabel} ${classes.centeredCell}`}>Quotes</Typography>
					<Typography className={`${classes.columnLabel} ${classes.centeredCell}`}>Invoices</Typography>
					<span />
				</div>
				{customers.length === 0 && <div className={classes.emptyState}>No wholesale customers yet.</div>}
				{customers.map((customer) => (
					<div key={customer.id} className={classes.customerListRow}>
						<div>
							<Typography className={classes.rowTitle}>{customer.company_name || customer.business_name}</Typography>
							<Typography className={classes.rowMeta}>{customer.locations?.[0]?.address_1 || 'No default location yet'}</Typography>
						</div>
						<Typography className={`${classes.rowTitle} ${classes.centeredCell}`}>{getCustomerQuoteCount(customer.id)}</Typography>
						<Typography className={`${classes.rowTitle} ${classes.centeredCell}`}>{getCustomerInvoiceCount(customer.id)}</Typography>
						<Button variant='outlined' color='primary' onClick={() => editCustomer(customer)} className={classes.editButton}>Edit</Button>
					</div>
				))}
			</div>
			<Modal open={customerModalOpen} onClose={closeCustomerModal}>
				{renderCustomerForm()}
			</Modal>
		</React.Fragment>
	);

	const renderProductForm = () => (
		<form onSubmit={saveProduct} className={classes.formModalPaper} style={{ width: 'min(520px, calc(100vw - 32px))' }}>
			<div className={classes.formModalHeader}>
				<div>
					<Typography className={classes.sectionTitle}>{editingProductId ? 'Edit product' : 'New product'}</Typography>
					<Typography className={classes.sectionHint}>
						Product price is the base price for quotes and invoices.
					</Typography>
				</div>
				<Button variant='text' className={classes.closeIconButton} onClick={closeProductModal} aria-label='Close'>
					×
				</Button>
			</div>
			<div className={classes.formModalBody}>
				{productError && <div className={classes.error}>{productError}</div>}
				<TextField
					className={classes.compactField}
					label='Name'
					value={productForm.item_name}
					onChange={(event) => {
						setProductForm({ ...productForm, item_name: event.target.value });
						if (productError) {
							setProductError('');
						}
					}}
					variant='outlined'
					size='small'
					fullWidth
					required
				/>
				<div className={classes.fieldRow}>
					<TextField
						className={classes.compactField}
						label='Item code'
						value={productForm.item_code}
						onChange={(event) => setProductForm({ ...productForm, item_code: event.target.value })}
						variant='outlined'
						size='small'
						fullWidth
					/>
					<TextField
						className={classes.compactField}
						label='UPC code'
						value={productForm.upc_code}
						onChange={(event) => setProductForm({ ...productForm, upc_code: event.target.value })}
						variant='outlined'
						size='small'
						fullWidth
					/>
				</div>
				<div className={classes.fieldRow}>
					<TextField
						className={classes.compactField}
						label='Price'
						value={productForm.price}
						onChange={(event) => {
							setProductForm({ ...productForm, price: event.target.value });
							if (productError) {
								setProductError('');
							}
						}}
						variant='outlined'
						size='small'
						fullWidth
						InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
						inputProps={{ inputMode: 'decimal' }}
					/>
					<TextField
						className={classes.compactField}
						label='Unit weight'
						value={productForm.unit_weight}
						onChange={(event) => setProductForm({ ...productForm, unit_weight: event.target.value })}
						variant='outlined'
						size='small'
						fullWidth
						inputProps={{ inputMode: 'decimal' }}
					/>
				</div>
			</div>
			<div className={classes.formModalFooter}>
				<span />
				<div className={classes.formModalActions}>
					<Button variant='outlined' className={classes.secondaryButton} onClick={closeProductModal}>
						Cancel
					</Button>
					<Button type='submit' variant='contained' disabled={savingProduct} className={classes.primaryButton}>
						{savingProduct ? 'Saving...' : editingProductId ? 'Save product' : 'Add product'}
					</Button>
				</div>
			</div>
		</form>
	);

	const renderProducts = () => (
		<React.Fragment>
			<div>
				<div className={classes.productTableHeader}>
					<Typography className={classes.columnLabel}>Name</Typography>
					<Typography className={classes.columnLabel}>Code</Typography>
					<Typography className={classes.columnLabel}>UPC</Typography>
					<Typography className={classes.columnLabel}>Price</Typography>
					<span />
				</div>
				{products.length === 0 && (
					<div className={classes.emptyState}>No wholesale products yet.</div>
				)}
				{products.map((product) => (
					<div key={product.id} className={classes.productListRow}>
						<div>
							<Typography className={classes.rowTitle}>{product.item_name}</Typography>
						</div>
						<Typography className={classes.rowTitle}>{product.item_code || 'No code'}</Typography>
						<Typography className={classes.rowMeta}>{product.upc_code || 'No UPC'}</Typography>
						<Typography className={classes.rowMeta}>${product.price}</Typography>
						<Button
							variant='outlined'
							color='primary'
							onClick={() => editProduct(product)}
							className={classes.iconEditButton}
						>
							<EditIcon fontSize='small' />
						</Button>
					</div>
				))}
			</div>
			<Modal open={productModalOpen} onClose={closeProductModal}>
				{renderProductForm()}
			</Modal>
		</React.Fragment>
	);

	const selectedQuoteCustomer = customers.find(
		(customer) => customer.id.toString() === quoteForm.customer_id,
	);

	const getCustomerQuoteCount = (customerId) =>
		quotes.filter((quote) => quote.customer_id === customerId).length;

	const getCustomerInvoiceCount = (customerId) =>
		invoices.filter(
			(invoice) =>
				invoice.customer_id === customerId &&
				invoice.status === 'Open',
		).length;

	const renderCustomerFields = ({ label, form, selectedCustomer, onCustomerChange, onLocationChange }) => (
		<div className={classes.fieldRow}>
			<TextField
				select
				SelectProps={{ native: true }}
				className={classes.compactField}
				label={label}
				value={form.customer_id}
				onChange={(event) => onCustomerChange(event.target.value)}
				variant='outlined'
				size='small'
				fullWidth
				required
			>
				<option value='' />
				{customers.map((customer) => (
					<option key={customer.id} value={customer.id}>
						{customer.company_name || customer.business_name}
					</option>
				))}
			</TextField>
			<TextField
				select
				SelectProps={{ native: true }}
				className={classes.compactField}
				label='Location'
				value={form.customer_location_id}
				onChange={(event) => onLocationChange(event.target.value)}
				variant='outlined'
				size='small'
				fullWidth
				InputLabelProps={selectedCustomer ? { shrink: true } : undefined}
			>
				<option value=''>{selectedCustomer ? 'Default' : ''}</option>
				{(selectedCustomer?.locations || []).map((location) => (
					<option key={location.id} value={location.id}>
						Location {location.location_number}
						{location.address_1 ? ` - ${location.address_1}` : ''}
					</option>
				))}
			</TextField>
		</div>
	);

	const renderItemsEditor = ({ items, onProductChange, onItemChange, onAdd, onRemove }) => (
		<div>
			<div className={classes.itemsEditorHeader}>
				<Typography className={classes.columnLabel}>Item</Typography>
				<Typography className={classes.columnLabel}>Qty</Typography>
				<Typography className={classes.columnLabel}>Price</Typography>
				<Typography className={classes.columnLabel} style={{ textAlign: 'right' }}>
					Total
				</Typography>
				<span />
			</div>
			{items.map((item, index) => (
				<div key={index} className={classes.itemsEditorRow}>
					<TextField
						select
						SelectProps={{ native: true }}
						value={item.product_id ? item.product_id.toString() : ''}
						onChange={(event) => onProductChange(index, event.target.value)}
						variant='outlined'
						size='small'
						fullWidth
						inputProps={{ 'aria-label': 'Product' }}
					>
						<option value=''>Choose a product</option>
						{products.map((product) => (
							<option key={product.id} value={product.id}>
								{product.item_name}
							</option>
						))}
					</TextField>
					<TextField
						value={item.amount}
						onChange={(event) => onItemChange(index, 'amount', event.target.value)}
						variant='outlined'
						size='small'
						inputProps={{ 'aria-label': 'Quantity', inputMode: 'decimal' }}
					/>
					<TextField
						value={item.unit_price}
						onChange={(event) => onItemChange(index, 'unit_price', event.target.value)}
						variant='outlined'
						size='small'
						InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
						inputProps={{ 'aria-label': 'Price', inputMode: 'decimal' }}
					/>
					<Typography className={classes.lineTotal}>
						{formatCurrency(Number(item.amount || 0) * Number(item.unit_price || 0))}
					</Typography>
					{items.length > 1 ? (
						<Button className={classes.removeLineButton} onClick={() => onRemove(index)} aria-label='Remove item'>
							<CloseIcon style={{ fontSize: 16 }} />
						</Button>
					) : (
						<span />
					)}
				</div>
			))}
			<Button className={classes.addItemButton} onClick={onAdd}>
				+ Add item
			</Button>
		</div>
	);

	const renderTotals = ({ items, shipping, onShippingChange }) => {
		const subtotal = itemsSubtotal(items);
		return (
			<div className={classes.totals}>
				<div className={classes.totalsLine}>
					<Typography className={classes.totalsLabel}>Subtotal</Typography>
					<Typography className={classes.totalsValue}>{formatCurrency(subtotal)}</Typography>
				</div>
				<div className={classes.totalsLine}>
					<Typography className={classes.totalsLabel}>Shipping & handling</Typography>
					<TextField
						value={shipping}
						onChange={(event) => onShippingChange(event.target.value)}
						variant='outlined'
						size='small'
						className={classes.shippingField}
						InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
						inputProps={{ 'aria-label': 'Shipping and handling', inputMode: 'decimal' }}
					/>
				</div>
				<div className={classes.totalsGrand}>
					<Typography className={classes.totalsGrandLabel}>Total</Typography>
					<Typography className={classes.totalsGrandValue}>
						{formatCurrency(subtotal + Number(shipping || 0))}
					</Typography>
				</div>
			</div>
		);
	};

	const editingQuote = quotes.find((quote) => quote.id === editingQuoteId);

	const renderQuoteFooterStart = () => {
		if (!editingQuote) {
			return <span />;
		}
		if (quoteToDelete) {
			return (
				<div className={classes.deleteConfirm}>
					<Typography className={classes.deleteConfirmText}>Delete this quote?</Typography>
					<Button className={classes.secondaryButton} variant='outlined' onClick={closeDeleteQuote}>
						Keep
					</Button>
					<Button className={classes.dangerButton} variant='contained' onClick={deleteQuote} disabled={savingQuote}>
						{savingQuote ? 'Deleting...' : 'Delete'}
					</Button>
				</div>
			);
		}
		return (
			<Button className={classes.dangerTextButton} onClick={() => openDeleteQuote(editingQuote)}>
				<DeleteIcon style={{ fontSize: 16, marginRight: 6 }} />
				Delete quote
			</Button>
		);
	};

	const renderDetail = (label, value) => (
		<div className={classes.detailField}>
			<Typography className={classes.detailLabel}>{label}</Typography>
			<Typography className={classes.detailValue}>{value || '—'}</Typography>
		</div>
	);

	const renderQuoteDetails = () => {
		if (!viewQuote) {
			return <span />;
		}
		const customerName = viewQuote.company_name || viewQuote.business_name || 'Unknown customer';
		const location = viewQuote.location_number
			? `Location ${viewQuote.location_number}${viewQuote.location_address_1 ? ` – ${viewQuote.location_address_1}` : ''}`
			: 'Default';
		const items = viewQuote.items || [];

		return (
			<div className={classes.formModalPaper}>
				<div className={classes.formModalHeader}>
					<div>
						<Typography className={classes.sectionTitle}>
							{quoteDisplayNumber(viewQuote)} · {customerName}
						</Typography>
						<Typography className={classes.sectionHint}>
							Created {formatDate(viewQuote.created_at)} ·{' '}
							{viewQuote.emailed_at ? `Sent ${formatDate(viewQuote.emailed_at)}` : 'Not sent'}
						</Typography>
					</div>
					<Button variant='text' className={classes.closeIconButton} onClick={() => setViewQuote(null)} aria-label='Close'>
						×
					</Button>
				</div>
				<div className={classes.formModalBody}>
					<div className={classes.detailGrid}>
						{renderDetail('Customer', customerName)}
						{renderDetail('Location', location)}
						{renderDetail('Terms', viewQuote.payment_terms ? viewQuote.payment_terms.replace('net', 'Net ') : '')}
						{renderDetail('Expected delivery', viewQuote.expected_delivery_date ? formatDate(viewQuote.expected_delivery_date) : '')}
					</div>
					<div className={classes.detailItemsHeader}>
						<Typography className={classes.columnLabel}>Item</Typography>
						<Typography className={classes.columnLabel} style={{ textAlign: 'right' }}>Qty</Typography>
						<Typography className={classes.columnLabel} style={{ textAlign: 'right' }}>Price</Typography>
						<Typography className={classes.columnLabel} style={{ textAlign: 'right' }}>Total</Typography>
					</div>
					{items.length === 0 && <div className={classes.emptyState}>No items on this quote.</div>}
					{items.map((item, index) => (
						<div key={item.id || index} className={classes.detailItemRow}>
							<Typography className={classes.rowTitle} style={{ fontSize: 13 }}>{item.product_name}</Typography>
							<Typography className={classes.lineTotal}>{formatQuantity(item.amount)}</Typography>
							<Typography className={classes.lineTotal}>{formatCurrency(item.unit_price)}</Typography>
							<Typography className={classes.lineTotal}>{formatCurrency(item.line_total)}</Typography>
						</div>
					))}
					<div className={classes.totals}>
						<div className={classes.totalsLine}>
							<Typography className={classes.totalsLabel}>Subtotal</Typography>
							<Typography className={classes.totalsValue}>{formatCurrency(viewQuote.subtotal)}</Typography>
						</div>
						<div className={classes.totalsLine}>
							<Typography className={classes.totalsLabel}>Shipping & handling</Typography>
							<Typography className={classes.totalsValue}>{formatCurrency(viewQuote.shipping_handling)}</Typography>
						</div>
						<div className={classes.totalsGrand}>
							<Typography className={classes.totalsGrandLabel}>Total</Typography>
							<Typography className={classes.totalsGrandValue}>{formatCurrency(viewQuote.total)}</Typography>
						</div>
					</div>
					{viewQuote.notes && (
						<React.Fragment>
							<Typography className={classes.formSectionLabel}>Notes</Typography>
							<Typography className={classes.detailNotes}>{viewQuote.notes}</Typography>
						</React.Fragment>
					)}
				</div>
				<div className={classes.formModalFooter}>
					<Button
						variant='outlined'
						className={classes.secondaryButton}
						onClick={() => {
							setViewQuote(null);
							openQuotePdf(viewQuote);
						}}
					>
						View PDF
					</Button>
					{!isArchivedYear && (
						<div className={classes.formModalActions}>
							<Button
								variant='outlined'
								className={classes.secondaryButton}
								onClick={() => {
									setViewQuote(null);
									editQuote(viewQuote);
								}}
							>
								<EditIcon style={{ fontSize: 15, marginRight: 6 }} />
								Edit
							</Button>
							<Button
								variant='contained'
								className={classes.primaryButton}
								onClick={() => {
									setViewQuote(null);
									openNewOrder(viewQuote);
								}}
							>
								<AddShoppingCartIcon style={{ fontSize: 15, marginRight: 6 }} />
								Start order
							</Button>
						</div>
					)}
				</div>
			</div>
		);
	};

	const renderQuoteForm = () => (
		<form onSubmit={saveQuote} className={classes.formModalPaper}>
			<div className={classes.formModalHeader}>
				<div>
					<Typography className={classes.sectionTitle}>
						{editingQuote ? `Edit ${quoteDisplayNumber(editingQuote)}` : 'New quote'}
					</Typography>
					<Typography className={classes.sectionHint}>
						{editingQuote
							? `Created ${formatDate(editingQuote.created_at)}`
							: "Picking a customer fills in their last quote's items."}
					</Typography>
				</div>
				<Button variant='text' className={classes.closeIconButton} onClick={closeQuoteModal} aria-label='Close'>
					×
				</Button>
			</div>
			<div className={classes.formModalBody}>
				{quoteError && <div className={classes.error}>{quoteError}</div>}
				{renderCustomerFields({
					label: 'Customer',
					form: quoteForm,
					selectedCustomer: selectedQuoteCustomer,
					onCustomerChange: updateQuoteCustomer,
					onLocationChange: (locationId) => setQuoteForm({ ...quoteForm, customer_location_id: locationId }),
				})}
				{renderItemsEditor({
					items: quoteForm.items,
					onProductChange: updateQuoteProduct,
					onItemChange: updateQuoteItem,
					onAdd: addQuoteItem,
					onRemove: removeQuoteItem,
				})}
				{renderTotals({
					items: quoteForm.items,
					shipping: quoteForm.shipping_handling,
					onShippingChange: (value) => setQuoteForm({ ...quoteForm, shipping_handling: value }),
				})}
				<Typography className={classes.formSectionLabel}>Details</Typography>
				<div className={classes.fieldRow}>
					<TextField
						select
						SelectProps={{ native: true }}
						className={classes.compactField}
						label='Terms'
						value={quoteForm.payment_terms}
						onChange={(event) => setQuoteForm({ ...quoteForm, payment_terms: event.target.value })}
						variant='outlined'
						size='small'
						fullWidth
					>
						{paymentTerms.map((term) => (
							<option key={term} value={term}>
								{term.replace('net', 'Net ')}
							</option>
						))}
					</TextField>
					<TextField
						className={classes.compactField}
						label='Expected delivery'
						type='date'
						value={quoteForm.expected_delivery_date}
						onChange={(event) =>
							setQuoteForm({ ...quoteForm, expected_delivery_date: event.target.value })
						}
						variant='outlined'
						size='small'
						fullWidth
						InputLabelProps={{ shrink: true }}
					/>
				</div>
				<TextField
					label='Notes'
					value={quoteForm.notes}
					onChange={(event) => setQuoteForm({ ...quoteForm, notes: event.target.value })}
					variant='outlined'
					size='small'
					fullWidth
					multiline
					minRows={2}
				/>
			</div>
			<div className={classes.formModalFooter}>
				{renderQuoteFooterStart()}
				{!quoteToDelete && (
					<div className={classes.formModalActions}>
						<Button variant='outlined' className={classes.secondaryButton} onClick={closeQuoteModal}>
							Cancel
						</Button>
						<Button type='submit' variant='contained' disabled={savingQuote} className={classes.primaryButton}>
							{savingQuote ? 'Saving...' : editingQuote ? 'Save quote' : 'Create quote'}
						</Button>
					</div>
				)}
			</div>
		</form>
	);

	const formatQuantity = (value) =>
		Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

	const renderItemsTooltip = (items) => (
		<div>
			<Typography className={classes.itemsTooltipTitle}>
				{items.length} {items.length === 1 ? 'item' : 'items'}
			</Typography>
			{items.map((item, index) => (
				<div key={item.id || index} className={classes.itemsTooltipRow}>
					<span>{item.product_name}</span>
					<span className={classes.itemsTooltipQty}>{formatQuantity(item.amount)}</span>
				</div>
			))}
		</div>
	);

	const renderNumberWithItems = (number, items) => (
		<Tooltip
			title={renderItemsTooltip(items || [])}
			placement='bottom-start'
			classes={{ tooltip: classes.itemsTooltip }}
		>
			<Typography className={`${classes.rowTitle} ${classes.hoverNumber}`}>{number}</Typography>
		</Tooltip>
	);

	const visibleQuotes = quotes
		.slice()
		.sort((firstQuote, secondQuote) => {
			const multiplier = quoteSort.direction === 'asc' ? 1 : -1;
			if (quoteSort.field === 'customer') {
				const firstCustomer = (firstQuote.company_name || firstQuote.business_name || '').toLowerCase();
				const secondCustomer = (secondQuote.company_name || secondQuote.business_name || '').toLowerCase();
				return firstCustomer.localeCompare(secondCustomer) * multiplier;
			}
			if (quoteSort.field === 'amount') {
				return (Number(firstQuote.total || 0) - Number(secondQuote.total || 0)) * multiplier;
			}

			const firstDate = new Date(firstQuote.created_at || 0).getTime();
			const secondDate = new Date(secondQuote.created_at || 0).getTime();
			return (firstDate - secondDate) * multiplier;
		});

	const renderYearSelector = () => (
		<TextField
			select
			SelectProps={{ native: true }}
			value={wholesaleYear}
			onChange={(event) => setWholesaleYear(Number(event.target.value))}
			variant='outlined'
			size='small'
			className={classes.yearSelect}
			inputProps={{ 'aria-label': 'Wholesale year' }}
		>
			{wholesaleYearOptions.map((year) => (
				<option key={year} value={year}>
					{year}
					{year === currentWholesaleYear ? ' (active)' : ''}
				</option>
			))}
		</TextField>
	);

	const renderQuotes = () => (
		<React.Fragment>
			{quoteError && <div className={classes.error}>{quoteError}</div>}
			<div>
				<div className={classes.tableHeader}>
					{renderSortHeader('customer', 'Customer')}
					<Typography className={classes.columnLabel}>Quote</Typography>
					{renderSortHeader('amount', 'Amount')}
					{renderSortHeader('created', 'Created')}
					<Typography className={classes.columnLabel}>Status</Typography>
					<span />
					<span />
					<span />
				</div>
				{visibleQuotes.length === 0 && <div className={classes.emptyState}>No wholesale quotes yet.</div>}
				{visibleQuotes.map((quote) => {
					const customerName = quote.company_name || quote.business_name || 'Unknown customer';
					return (
					<div key={quote.id} className={classes.row}>
						<div className={classes.customerCell}>
							<Button className={classes.nameLink} onClick={() => setViewQuote(quote)}>
								{customerName}
							</Button>
						</div>
						<div>{renderNumberWithItems(quoteDisplayNumber(quote), quote.items)}</div>
						<Typography className={classes.rowTitle}>{formatCurrency(quote.total)}</Typography>
						<Typography className={classes.rowMeta} style={{ marginTop: 0 }}>
							{formatDate(quote.created_at)}
						</Typography>
						<span
							className={classes.sentStatus}
							style={
								quote.emailed_at
									? { background: '#dff4e6', color: '#1f6f47' }
									: { background: '#efeee9', color: '#737873' }
							}
						>
							{quote.emailed_at ? `Sent ${formatDate(quote.emailed_at)}` : 'Not sent'}
						</span>
						<Button
							variant='outlined'
							className={classes.editButton}
							onClick={() => openQuotePdf(quote)}
						>
							View PDF
						</Button>
						<Tooltip title='Edit quote'>
							<span className={classes.tooltipTarget}>
								<Button
									variant='outlined'
									className={classes.iconEditButton}
									onClick={() => editQuote(quote)}
									aria-label={`Edit ${quoteDisplayNumber(quote)}`}
									disabled={isArchivedYear}
								>
									<EditIcon style={{ fontSize: 14 }} />
								</Button>
							</span>
						</Tooltip>
						<Tooltip title='Start order'>
							<span className={classes.tooltipTarget}>
								<Button
									variant='outlined'
									className={classes.iconEditButton}
									onClick={() => openNewOrder(quote)}
									aria-label={`Start order from ${quoteDisplayNumber(quote)}`}
									disabled={isArchivedYear}
								>
									<AddShoppingCartIcon style={{ fontSize: 14 }} />
								</Button>
							</span>
						</Tooltip>
					</div>
					);
				})}
			</div>
			<Modal open={Boolean(pdfQuote)} onClose={closeQuotePdf}>
				<div className={classes.pdfModalPaper}>
					<div className={classes.pdfToolbar}>
						<div>
							<Typography className={classes.rowTitle}>
								{pdfQuote?.quote_number || 'Quote Preview'}
							</Typography>
							<Typography className={classes.rowMeta}>
								{pdfQuote ? pdfQuote.company_name || pdfQuote.business_name : ''}
							</Typography>
						</div>
						<div className={classes.pdfActions}>
							<Button
								variant='outlined'
								className={classes.iconEditButton}
								href={pdfQuote ? `/api/wholesale/quotes/${pdfQuote.id}/pdf?download=1` : undefined}
								aria-label='Download quote PDF'
							>
								<GetAppIcon style={{ fontSize: 14 }} />
							</Button>
							<Button
								variant='outlined'
								className={classes.iconEditButton}
								onClick={emailQuote}
								aria-label='Email quote PDF'
							>
								<EmailIcon style={{ fontSize: 14 }} />
							</Button>
							<Button variant='outlined' className={classes.closeIconButton} onClick={closeQuotePdf} aria-label='Close quote preview'>
								×
							</Button>
						</div>
					</div>
					{pdfQuote && (
						<iframe
							title={`Quote ${pdfQuote.quote_number}`}
							className={classes.pdfFrame}
							src={`/api/wholesale/quotes/${pdfQuote.id}/pdf`}
						/>
					)}
				</div>
			</Modal>
			<Modal open={Boolean(viewQuote)} onClose={() => setViewQuote(null)}>
				{renderQuoteDetails()}
			</Modal>
			<Modal open={quoteModalOpen} onClose={closeQuoteModal}>
				{renderQuoteForm()}
			</Modal>
		</React.Fragment>
	);

	const selectedOrderCustomer = customers.find((customer) => customer.id.toString() === orderForm.customer_id);
	const editingOrder = orders.find((order) => order.id === editingOrderId);
	const pickListOrders = orders.filter((order) => order.status === 'Picking' || order.status === 'Packed');

	const orderCustomerName = (order) => order.company_name || order.business_name || 'Unknown customer';

	const renderOrderStatus = (order) => (
		<div className={classes.statusCell}>
			<span className={classes.sentStatus} style={orderStatusStyles[order.status] || orderStatusStyles.Received}>
				{order.status}
			</span>
			{order.adjusted && <span className={classes.adjustedTag}>Adjusted</span>}
		</div>
	);

	const orderLineItems = (order) => order.items.map((item) => ({ ...item, amount: item.ordered_qty }));

	const renderOrders = () => (
		<React.Fragment>
			{orderError && !orderModalOpen && !pickForm && <div className={classes.error}>{orderError}</div>}
			<div>
				<div className={classes.orderTableHeader}>
					<Typography className={classes.columnLabel}>Customer</Typography>
					<Typography className={classes.columnLabel}>Order</Typography>
					<Typography className={classes.columnLabel}>Received</Typography>
					<Typography className={classes.columnLabel}>Amount</Typography>
					<Typography className={classes.columnLabel}>Status</Typography>
					<span />
					<span />
				</div>
				{orders.length === 0 && <div className={classes.emptyState}>No orders yet.</div>}
				{orders.map((order) => (
					<div key={order.id} className={classes.orderRow}>
						<Typography className={classes.rowTitle}>{orderCustomerName(order)}</Typography>
						<div className={classes.statusCell}>
							{renderNumberWithItems(order.order_number, orderLineItems(order))}
							{order.attachments?.length > 0 && (
								<Tooltip title={order.attachments.map((attachment) => attachment.filename).join(', ')}>
									<AttachFileIcon className={classes.clipIcon} />
								</Tooltip>
							)}
						</div>
						<Typography className={classes.rowMeta} style={{ marginTop: 0 }}>
							{formatDate(order.received_date)}
							{order.received_via ? ` · ${order.received_via}` : ''}
						</Typography>
						<Typography className={classes.rowTitle}>{formatCurrency(order.total)}</Typography>
						<div className={classes.statusCell}>
							{renderOrderStatus(order)}
							{order.invoice_number && <span className={classes.rowMeta} style={{ marginTop: 0 }}>{order.invoice_number}</span>}
						</div>
						<Tooltip title='Edit order'>
							<span className={classes.tooltipTarget}>
								<Button
									variant='outlined'
									className={classes.iconEditButton}
									onClick={() => editOrder(order)}
									aria-label={`Edit ${order.order_number}`}
									disabled={isArchivedYear}
								>
									<EditIcon style={{ fontSize: 14 }} />
								</Button>
							</span>
						</Tooltip>
						<Tooltip title={order.status === 'Received' ? 'Send to pick list' : 'Open pick list'}>
							<span className={classes.tooltipTarget}>
								<Button
									variant='outlined'
									className={classes.iconEditButton}
									onClick={() => openPickList(order)}
									aria-label={`Pick list for ${order.order_number}`}
									disabled={isArchivedYear}
								>
									<AssignmentIcon style={{ fontSize: 15 }} />
								</Button>
							</span>
						</Tooltip>
					</div>
				))}
			</div>
		</React.Fragment>
	);

	const renderPickLists = () => (
		<React.Fragment>
			{orderError && !pickForm && <div className={classes.error}>{orderError}</div>}
			<div>
				<div className={classes.pickTableHeader}>
					<Typography className={classes.columnLabel}>Customer</Typography>
					<Typography className={classes.columnLabel}>Order</Typography>
					<Typography className={classes.columnLabel}>Lines</Typography>
					<Typography className={classes.columnLabel}>Delivery</Typography>
					<Typography className={classes.columnLabel}>Status</Typography>
					<span />
				</div>
				{pickListOrders.length === 0 && (
					<div className={classes.emptyState}>Nothing to pick. Send an order to the pick list from the Orders tab.</div>
				)}
				{pickListOrders.map((order) => (
					<div key={order.id} className={classes.pickRow}>
						<Typography className={classes.rowTitle}>{orderCustomerName(order)}</Typography>
						<div>{renderNumberWithItems(order.order_number, orderLineItems(order))}</div>
						<Typography className={classes.rowMeta} style={{ marginTop: 0 }}>
							{pluralize(order.items.length, 'item')}
						</Typography>
						<Typography className={classes.rowMeta} style={{ marginTop: 0 }}>
							{order.expected_delivery_date ? formatDate(order.expected_delivery_date) : '—'}
						</Typography>
						{renderOrderStatus(order)}
						<Button variant='outlined' className={classes.editButton} onClick={() => openPickList(order)}>
							Open
						</Button>
					</div>
				))}
			</div>
		</React.Fragment>
	);

	const attachmentUrl = (orderId, attachment, download = false) =>
		`/api/wholesale/orders/${orderId}/attachments/${attachment.id}${download ? '?download=1' : ''}`;

	const formatFileSize = (bytes) => {
		if (bytes >= 1024 * 1024) {
			return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
		}
		return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	};

	// Checks type and size up front so a bad file doesn't wait on the upload to fail.
	const acceptFiles = (fileList) => {
		const files = Array.from(fileList || []);
		const rejected = files.filter(
			(file) =>
				!attachmentExtensions.includes(`.${file.name.split('.').pop().toLowerCase()}`) ||
				file.size > MAX_ATTACHMENT_BYTES,
		);
		if (rejected.length > 0) {
			setOrderError(
				`${rejected.map((file) => file.name).join(', ')}: upload a PDF, image, or Word document up to 15 MB.`,
			);
		}
		return files.filter((file) => !rejected.includes(file));
	};

	const uploadFiles = async (orderId, files) => {
		for (const file of files) {
			await axios.post(`/api/wholesale/orders/${orderId}/attachments`, file, {
				headers: { 'Content-Type': file.type || 'application/octet-stream' },
				params: { filename: file.name },
			});
		}
	};

	const addOrderFiles = async (fileList) => {
		const files = acceptFiles(fileList);
		if (files.length === 0) {
			return;
		}
		if (!editingOrderId) {
			setPendingFiles((current) => [...current, ...files]);
			return;
		}
		setUploadingFiles(true);
		try {
			await uploadFiles(editingOrderId, files);
			await loadOrders();
		} catch (err) {
			setOrderError(err.response?.data?.message || 'Unable to upload the file.');
		}
		setUploadingFiles(false);
	};

	const removeOrderFile = async (attachment) => {
		if (!window.confirm(`Remove ${attachment.filename}?`)) {
			return;
		}
		try {
			await axios.delete(attachmentUrl(attachment.order_id, attachment));
			await loadOrders();
		} catch (err) {
			setOrderError(err.response?.data?.message || 'Unable to remove the file.');
		}
	};

	const renderAttachmentChip = (orderId, attachment, onRemove) => (
		<div key={attachment.id} className={classes.fileChip}>
			<InsertDriveFileIcon style={{ fontSize: 16, color: '#8a918a' }} />
			<a
				className={classes.fileLink}
				href={attachmentUrl(orderId, attachment)}
				target='_blank'
				rel='noopener noreferrer'
				title={`Open ${attachment.filename}`}
			>
				{attachment.filename}
			</a>
			<span className={classes.fileSize}>{formatFileSize(attachment.size_bytes)}</span>
			{onRemove && (
				<Button className={classes.fileRemove} onClick={onRemove} aria-label={`Remove ${attachment.filename}`}>
					<CloseIcon style={{ fontSize: 14 }} />
				</Button>
			)}
		</div>
	);

	const renderOrderFiles = () => {
		const savedFiles = editingOrder ? editingOrder.attachments || [] : [];
		return (
			<div className={classes.filesSection}>
				<Typography className={classes.formSectionLabel} style={{ marginTop: 6 }}>
					Customer's order
				</Typography>
				<div className={classes.fileList}>
					{savedFiles.map((attachment) =>
						renderAttachmentChip(editingOrder.id, attachment, () => removeOrderFile(attachment)),
					)}
					{pendingFiles.map((file, index) => (
						<div key={`${file.name}-${index}`} className={`${classes.fileChip} ${classes.fileChipPending}`}>
							<InsertDriveFileIcon style={{ fontSize: 16, color: '#8a918a' }} />
							<span className={classes.fileLink}>{file.name}</span>
							<span className={classes.fileSize}>{formatFileSize(file.size)} · uploads on save</span>
							<Button
								className={classes.fileRemove}
								onClick={() => setPendingFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))}
								aria-label={`Remove ${file.name}`}
							>
								<CloseIcon style={{ fontSize: 14 }} />
							</Button>
						</div>
					))}
				</div>
				<label
					className={`${classes.dropZone} ${dragActive ? classes.dropZoneActive : ''}`}
					onDragOver={(event) => {
						event.preventDefault();
						setDragActive(true);
					}}
					onDragLeave={() => setDragActive(false)}
					onDrop={(event) => {
						event.preventDefault();
						setDragActive(false);
						addOrderFiles(event.dataTransfer.files);
					}}
				>
					<input
						type='file'
						multiple
						accept={attachmentExtensions.join(',')}
						className={classes.hiddenInput}
						onChange={(event) => {
							addOrderFiles(event.target.files);
							event.target.value = '';
						}}
					/>
					<AttachFileIcon style={{ fontSize: 16 }} />
					{uploadingFiles ? 'Uploading...' : 'Drop the emailed, faxed, or photographed order here, or click to browse'}
					<span className={classes.dropZoneHint}>PDF, image, or Word · up to 15 MB</span>
				</label>
			</div>
		);
	};

	const renderOrderFooterStart = () => {
		if (!editingOrder) {
			return <span />;
		}
		if (editingOrder.invoice_id) {
			return <Typography className={classes.footerNote}>Invoiced as {editingOrder.invoice_number}</Typography>;
		}
		if (orderToDelete) {
			return (
				<div className={classes.deleteConfirm}>
					<Typography className={classes.deleteConfirmText}>Delete this order?</Typography>
					<Button className={classes.secondaryButton} variant='outlined' onClick={() => setOrderToDelete(null)}>
						Keep
					</Button>
					<Button className={classes.dangerButton} variant='contained' onClick={deleteOrder} disabled={savingOrder}>
						{savingOrder ? 'Deleting...' : 'Delete'}
					</Button>
				</div>
			);
		}
		return (
			<Button className={classes.dangerTextButton} onClick={() => setOrderToDelete(editingOrder)}>
				<DeleteIcon style={{ fontSize: 16, marginRight: 6 }} />
				Delete order
			</Button>
		);
	};

	const renderOrderForm = () => (
		<form onSubmit={saveOrder} className={classes.formModalPaper}>
			<div className={classes.formModalHeader}>
				<div>
					<Typography className={classes.sectionTitle}>
						{editingOrder ? `Edit ${editingOrder.order_number}` : 'New order'}
					</Typography>
					<Typography className={classes.sectionHint}>
						{orderForm.quote_number
							? `Prices from ${orderForm.quote_number}. Fill in the quantities the customer asked for.`
							: orderForm.customer_id
							? `No ${currentWholesaleYear} quote for this customer, so prices start from the product list.`
							: 'Pick a customer to load their quote prices.'}
					</Typography>
				</div>
				<Button variant='text' className={classes.closeIconButton} onClick={resetOrderModal} aria-label='Close'>
					×
				</Button>
			</div>
			<div className={classes.formModalBody}>
				{orderError && <div className={classes.error}>{orderError}</div>}
				{renderCustomerFields({
					label: 'Customer',
					form: orderForm,
					selectedCustomer: selectedOrderCustomer,
					onCustomerChange: updateOrderCustomer,
					onLocationChange: (locationId) => setOrderForm({ ...orderForm, customer_location_id: locationId }),
				})}
				<div className={classes.receivedRow}>
					<Typography className={classes.deliveryLabel} style={{ width: 'auto', whiteSpace: 'nowrap' }}>
						Received by
					</Typography>
					<div className={classes.segmented} role='group'>
						{receivedViaOptions.map((option) => (
							<Button
								key={option}
								className={`${classes.segmentButton} ${orderForm.received_via === option ? classes.segmentButtonActive : ''}`}
								onClick={() => setOrderForm({ ...orderForm, received_via: option })}
								aria-pressed={orderForm.received_via === option}
							>
								{option}
							</Button>
						))}
					</div>
					<TextField
						label='Received on'
						type='date'
						value={orderForm.received_date}
						onChange={(event) => setOrderForm({ ...orderForm, received_date: event.target.value })}
						variant='outlined'
						size='small'
						InputLabelProps={{ shrink: true }}
						className={classes.receivedDate}
					/>
				</div>
				{renderOrderFiles()}
				{renderItemsEditor({
					items: orderForm.items,
					onProductChange: updateOrderProduct,
					onItemChange: updateOrderItem,
					onAdd: addOrderItem,
					onRemove: removeOrderItem,
				})}
				{renderTotals({
					items: orderForm.items,
					shipping: orderForm.shipping_handling,
					onShippingChange: (value) => setOrderForm({ ...orderForm, shipping_handling: value }),
				})}
				<Typography className={classes.formSectionLabel}>Details</Typography>
				<div className={classes.fieldRow}>
					<TextField
						select
						SelectProps={{ native: true }}
						className={classes.compactField}
						label='Terms'
						value={orderForm.payment_terms}
						onChange={(event) => setOrderForm({ ...orderForm, payment_terms: event.target.value })}
						variant='outlined'
						size='small'
						fullWidth
					>
						{paymentTerms.map((term) => (
							<option key={term} value={term}>
								{term.replace('net', 'Net ')}
							</option>
						))}
					</TextField>
					<TextField
						className={classes.compactField}
						label='Expected delivery'
						type='date'
						value={orderForm.expected_delivery_date}
						onChange={(event) => setOrderForm({ ...orderForm, expected_delivery_date: event.target.value })}
						variant='outlined'
						size='small'
						fullWidth
						InputLabelProps={{ shrink: true }}
					/>
				</div>
				<TextField
					label='Notes'
					value={orderForm.notes}
					onChange={(event) => setOrderForm({ ...orderForm, notes: event.target.value })}
					variant='outlined'
					size='small'
					fullWidth
					multiline
					minRows={2}
				/>
			</div>
			<div className={classes.formModalFooter}>
				{renderOrderFooterStart()}
				{!orderToDelete && (
					<div className={classes.formModalActions}>
						<Button variant='outlined' className={classes.secondaryButton} onClick={resetOrderModal}>
							Cancel
						</Button>
						<Button
							type='submit'
							variant='outlined'
							disabled={savingOrder}
							className={!editingOrder || editingOrder.status === 'Received' ? classes.secondaryButton : classes.primaryButton}
						>
							{savingOrder ? 'Saving...' : 'Save order'}
						</Button>
						{(!editingOrder || editingOrder.status === 'Received') && (
							<Button
								variant='contained'
								disabled={savingOrder}
								className={classes.primaryButton}
								onClick={() => saveOrder(null, { sendToPicking: true })}
							>
								Save & send to pick list
							</Button>
						)}
					</div>
				)}
			</div>
		</form>
	);

	const pickQty = (item) => (item.pick_qty === '' || item.pick_qty === null ? Number(item.ordered_qty || 0) : Number(item.pick_qty));
	const packedQty = (item) => (item.picked_qty === '' || item.picked_qty === null ? pickQty(item) : Number(item.picked_qty));

	const renderPickList = () => {
		if (!pickForm) {
			return null;
		}
		const changedLines = pickForm.items.filter((item) => packedQty(item) !== Number(item.ordered_qty || 0)).length;
		const subtotal = pickForm.items.reduce((total, item) => total + packedQty(item) * Number(item.unit_price || 0), 0);
		const location = customers
			.find((customer) => customer.id === pickForm.customer_id)
			?.locations?.find((currentLocation) => currentLocation.id === pickForm.customer_location_id);
		const isPacked = pickForm.status === 'Packed';
		const isInvoiced = pickForm.status === 'Invoiced';

		return (
			<div className={classes.formModalPaper} style={{ width: 'min(900px, calc(100vw - 32px))' }}>
				<div className={classes.formModalHeader}>
					<div>
						<div className={classes.pickTitleRow}>
							<Typography className={classes.sectionTitle}>Pick list {pickForm.order_number}</Typography>
							{renderOrderStatus({ ...pickForm, adjusted: false })}
						</div>
						<Typography className={classes.sectionHint}>
							{orderCustomerName(pickForm)}
							{location ? ` · Location ${location.location_number}${location.address_1 ? ` – ${location.address_1}` : ''}` : ''}
							{pickForm.expected_delivery_date ? ` · Delivery ${formatDate(pickForm.expected_delivery_date)}` : ''}
						</Typography>
						{(pickForm.attachments || []).length > 0 && (
							<div className={classes.fileList} style={{ marginTop: 8, marginBottom: 0 }}>
								{pickForm.attachments.map((attachment) => renderAttachmentChip(pickForm.id, attachment))}
							</div>
						)}
					</div>
					<Button variant='text' className={classes.closeIconButton} onClick={() => setPickForm(null)} aria-label='Close'>
						×
					</Button>
				</div>
				<div className={classes.formModalBody}>
					{orderError && <div className={classes.error}>{orderError}</div>}
					<div className={classes.pickItemsHeader}>
						<Typography className={classes.columnLabel}>Item</Typography>
						<Typography className={classes.columnLabel} style={{ textAlign: 'right' }}>Ordered</Typography>
						<Typography className={classes.columnLabel}>To pick</Typography>
						<Typography className={classes.columnLabel}>Picked</Typography>
						<Typography className={classes.columnLabel}>Price</Typography>
						<Typography className={classes.columnLabel} style={{ textAlign: 'right' }}>Total</Typography>
					</div>
					{pickForm.items.map((item, index) => {
						const planChanged = pickQty(item) !== Number(item.ordered_qty || 0);
						const packChanged = packedQty(item) !== pickQty(item);
						return (
							<div
								key={index}
								className={`${classes.pickItemRow} ${planChanged || packChanged ? classes.pickItemChanged : ''}`}
							>
								{item.isNew ? (
									<TextField
										select
										SelectProps={{ native: true }}
										value={item.product_id}
										onChange={(event) => updatePickProduct(index, event.target.value)}
										variant='outlined'
										size='small'
										fullWidth
										inputProps={{ 'aria-label': 'Product' }}
									>
										<option value=''>Choose a product</option>
										{products.map((product) => (
											<option key={product.id} value={product.id}>
												{product.item_name}
											</option>
										))}
									</TextField>
								) : (
									<Typography className={classes.rowTitle} style={{ fontSize: 13 }}>
										{item.product_name}
									</Typography>
								)}
								<Typography className={classes.lineTotal}>{formatQuantity(item.ordered_qty)}</Typography>
								<TextField
									value={item.pick_qty}
									placeholder={formatQuantity(item.ordered_qty)}
									onChange={(event) => updatePickItem(index, 'pick_qty', event.target.value)}
									variant='outlined'
									size='small'
									className={planChanged ? classes.changedField : ''}
									inputProps={{ 'aria-label': 'To pick', inputMode: 'decimal' }}
								/>
								<TextField
									value={item.picked_qty}
									placeholder={formatQuantity(pickQty(item))}
									onChange={(event) => updatePickItem(index, 'picked_qty', event.target.value)}
									variant='outlined'
									size='small'
									className={packChanged ? classes.changedField : ''}
									inputProps={{ 'aria-label': 'Picked', inputMode: 'decimal' }}
								/>
								<TextField
									value={item.unit_price}
									onChange={(event) => updatePickItem(index, 'unit_price', event.target.value)}
									variant='outlined'
									size='small'
									InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
									inputProps={{ 'aria-label': 'Price', inputMode: 'decimal' }}
								/>
								<Typography className={classes.lineTotal}>
									{formatCurrency(packedQty(item) * Number(item.unit_price || 0))}
								</Typography>
							</div>
						);
					})}
					<Button className={classes.addItemButton} onClick={addPickItem}>
						+ Add item
					</Button>
					<div className={classes.pickSummary}>
						<div className={classes.pickNotes}>
							<TextField
								label='Notes'
								placeholder='e.g. Called Moshe, OK with 36 instead of 40'
								value={pickForm.notes}
								onChange={(event) => setPickForm({ ...pickForm, notes: event.target.value })}
								variant='outlined'
								size='small'
								fullWidth
								multiline
								minRows={3}
								InputLabelProps={{ shrink: true }}
							/>
						</div>
						<div className={classes.totals} style={{ marginTop: 0 }}>
							<div className={classes.totalsLine}>
								<Typography className={classes.totalsLabel}>Subtotal</Typography>
								<Typography className={classes.totalsValue}>{formatCurrency(subtotal)}</Typography>
							</div>
							<div className={classes.totalsLine}>
								<Typography className={classes.totalsLabel}>Shipping & handling</Typography>
								<TextField
									value={pickForm.shipping_handling}
									onChange={(event) => setPickForm({ ...pickForm, shipping_handling: event.target.value })}
									variant='outlined'
									size='small'
									className={classes.shippingField}
									InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
									inputProps={{ 'aria-label': 'Shipping and handling', inputMode: 'decimal' }}
								/>
							</div>
							<div className={classes.totalsGrand}>
								<Typography className={classes.totalsGrandLabel}>Total</Typography>
								<Typography className={classes.totalsGrandValue}>
									{formatCurrency(subtotal + Number(pickForm.shipping_handling || 0))}
								</Typography>
							</div>
						</div>
					</div>
				</div>
				<div className={classes.formModalFooter}>
					<Typography className={classes.footerNote}>
						{changedLines > 0
							? `${pluralize(changedLines, 'line')} changed from the order`
							: 'Matches the order'}
						{isInvoiced && pickForm.invoice_number ? ` · Invoiced as ${pickForm.invoice_number}` : ''}
					</Typography>
					<div className={classes.formModalActions}>
						{isPacked && (
							<Button className={classes.setupLink} onClick={() => runPickAction('picking')} disabled={savingOrder}>
								Back to picking
							</Button>
						)}
						<Button variant='outlined' className={classes.secondaryButton} onClick={() => runPickAction('save')} disabled={savingOrder}>
							Save
						</Button>
						{pickForm.status === 'Picking' && (
							<Button variant='contained' className={classes.primaryButton} onClick={() => runPickAction('packed')} disabled={savingOrder}>
								Mark packed
							</Button>
						)}
						{(isPacked || isInvoiced) && (
							<Tooltip title={isInvoiced && pickForm.invoice_number ? `Voids ${pickForm.invoice_number} and creates a new one` : ''}>
								<span>
									<Button variant='contained' className={classes.convertButton} onClick={() => runPickAction('invoice')} disabled={savingOrder}>
										{isInvoiced ? 'Create new invoice' : 'Create invoice'}
									</Button>
								</span>
							</Tooltip>
						)}
					</div>
				</div>
			</div>
		);
	};

	const renderOrderModals = () => (
		<React.Fragment>
			<Modal open={orderModalOpen} onClose={resetOrderModal}>
				{renderOrderForm()}
			</Modal>
			<Modal open={Boolean(pickForm)} onClose={() => setPickForm(null)}>
				{renderPickList() || <span />}
			</Modal>
		</React.Fragment>
	);

	const renderInvoices = () => (
		<React.Fragment>
			{invoiceError && <div className={classes.error}>{invoiceError}</div>}
			<div>
				<div className={classes.invoiceTableHeader}>
					<Typography className={classes.columnLabel}>Invoice</Typography>
					<Typography className={classes.columnLabel}>Customer</Typography>
					<Typography className={classes.columnLabel}>Amount</Typography>
					<Typography className={classes.columnLabel}>Due Date</Typography>
					<Typography className={classes.columnLabel}>Status</Typography>
					<span />
				</div>
				{invoices.length === 0 && <div className={classes.emptyState}>No wholesale invoices yet.</div>}
				{invoices.map((invoice) => {
					const customerName = invoice.company_name || invoice.business_name || 'Unknown customer';
					return (
					<div key={invoice.id} className={classes.invoiceRow}>
						<div>
							{renderNumberWithItems(invoice.invoice_number, invoice.items)}
							<Typography className={classes.rowMeta}>Order {invoice.order_number || '—'}</Typography>
						</div>
						<div className={classes.customerCell}>
							<Typography className={classes.rowTitle}>{customerName}</Typography>
						</div>
						<Typography className={classes.rowTitle}>{formatCurrency(invoice.total)}</Typography>
						<Typography className={classes.rowMeta}>{formatDate(invoice.due_date)}</Typography>
						<StatusChip value={invoice.status} />
						<Button variant='outlined' className={classes.editButton} onClick={() => openInvoicePdf(invoice)}>
							View PDF
						</Button>
					</div>
					);
				})}
			</div>
			<Modal open={Boolean(pdfInvoice)} onClose={closeInvoicePdf}>
				<div className={classes.pdfModalPaper}>
					<div className={classes.pdfToolbar}>
						<div>
							<Typography className={classes.rowTitle}>
								{pdfInvoice?.invoice_number || 'Invoice Preview'}
							</Typography>
							<Typography className={classes.rowMeta}>
								{pdfInvoice ? pdfInvoice.company_name || pdfInvoice.business_name : ''}
							</Typography>
						</div>
						<div className={classes.pdfActions}>
							<Button
								variant='outlined'
								className={classes.iconEditButton}
								href={pdfInvoice ? `/api/wholesale/invoices/${pdfInvoice.id}/pdf?download=1` : undefined}
								aria-label='Download invoice PDF'
							>
								<GetAppIcon style={{ fontSize: 14 }} />
							</Button>
							<Button
								variant='outlined'
								className={classes.iconEditButton}
								onClick={emailInvoice}
								aria-label='Email invoice PDF'
							>
								<EmailIcon style={{ fontSize: 14 }} />
							</Button>
							<Button variant='outlined' className={classes.closeIconButton} onClick={closeInvoicePdf} aria-label='Close invoice preview'>
								×
							</Button>
						</div>
					</div>
					{pdfInvoice && (
						<iframe
							title={`Invoice ${pdfInvoice.invoice_number}`}
							className={classes.pdfFrame}
							src={`/api/wholesale/invoices/${pdfInvoice.id}/pdf`}
						/>
					)}
				</div>
			</Modal>
		</React.Fragment>
	);

	const isSetupView = tab >= 4;

	const renderPrimaryAction = () => {
		if (tab === 4) {
			return (
				<Button variant='outlined' className={classes.primaryButton} onClick={openNewCustomer}>
					+ Add customer
				</Button>
			);
		}
		if (tab === 5) {
			return (
				<Button variant='outlined' className={classes.primaryButton} onClick={openNewProduct}>
					+ Add product
				</Button>
			);
		}
		if (isArchivedYear || tab === 2) {
			return null;
		}
		if (tab === 1) {
			return (
				<Button variant='outlined' className={classes.primaryButton} onClick={() => openNewOrder()}>
					+ New order
				</Button>
			);
		}
		if (tab === 3) {
			return (
				<Button variant='outlined' className={classes.secondaryButton}>
					Record payment
				</Button>
			);
		}
		return (
			<Button variant='outlined' className={classes.primaryButton} onClick={openNewQuote}>
				+ New quote
			</Button>
		);
	};

	const primaryAction = renderPrimaryAction();

	return (
		<div className={classes.page}>
			<div className={classes.shell}>
				<div className={classes.header}>
					<div className={classes.headerLeft}>
						<Button className={classes.titleButton} onClick={() => changeTab(0)}>
							Wholesale
						</Button>
						{renderYearSelector()}
						<Tabs
							value={isSetupView ? false : tab}
							action={tabsActions}
							onChange={(event, nextTab) => changeTab(nextTab)}
							className={classes.tabs}
						>
							<Tab className={classes.tab} label='Quotes' />
							<Tab className={classes.tab} label='Orders' />
							<Tab
								className={classes.tab}
								label={
									<span>
										Pick lists
										{openPickCount > 0 && <span className={classes.tabCount}>{openPickCount}</span>}
									</span>
								}
							/>
							<Tab className={classes.tab} label='Invoices' />
						</Tabs>
						{isArchivedYear && <Typography className={classes.archivedNote}>Past year · read only</Typography>}
					</div>
					<div className={classes.headerActions}>
						<Button
							className={`${classes.setupLink} ${tab === 4 ? classes.setupLinkActive : ''}`}
							onClick={() => changeTab(4)}
						>
							Customers
						</Button>
						<Button
							className={`${classes.setupLink} ${tab === 5 ? classes.setupLinkActive : ''}`}
							onClick={() => changeTab(5)}
						>
							Products
						</Button>
						{primaryAction && <span className={classes.setupDivider} />}
						{primaryAction}
					</div>
				</div>

				{tab === 0 && renderQuotes()}
				{tab === 1 && renderOrders()}
				{tab === 2 && renderPickLists()}
				{tab === 3 && renderInvoices()}
				{tab === 4 && renderCustomers()}
				{tab === 5 && renderProducts()}
				{renderOrderModals()}
			</div>
		</div>
	);
}

export default withStyles(styles)(WholesalePage);
